/**
 * Gate 1 — Step 5: Test Cases (Figma-aligned layout).
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TRUST_ATTRIBUTES, trustAttributeLabel } from "../../config/trustAttributes";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TruncatedCellLine from "../../components/TruncatedCellLine";
import { ReactComponent as FilterIcon } from "../../asserts/logo/filter.svg";
import { ReactComponent as TrustAttributesIcon } from "../../asserts/logo/shield.svg";
import { ReactComponent as RisksWarningIcon } from "../../asserts/logo/alert-triangle.svg";
import { ReactComponent as MetricsIcon } from "../../asserts/logo/metrics_icon.svg";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { ReactComponent as TestStrategyIcon } from "../../asserts/logo/TestStrategyIcon.svg";
import { ReactComponent as TestCasesIcon } from "../../asserts/logo/TestCasesIcon.svg";
import { ReactComponent as TestSuiteIcon } from "../../asserts/logo/TestSuiteIcon.svg";
import { ReactComponent as SearchIcon } from "../../asserts/logo/Search.svg";
import { ReactComponent as ThreeDotsIcon } from "../../asserts/logo/3dots.svg";
import { ReactComponent as BlackFilledExpandIcon } from "../../asserts/logo/BlackFilledExpandIcon.svg";
import { ReactComponent as DeleteIcon } from "../../asserts/logo/delete.svg";
import { ReactComponent as CheckBoxUncheckedIcon } from "../../asserts/logo/Check_box.svg";
import { ReactComponent as PauseCircleIcon } from "../../asserts/logo/pause-circle.svg";
import { ReactComponent as PlayCircleIcon } from "../../asserts/logo/play-circle.svg";
import { ReactComponent as KeyboardArrowDownIcon } from "../../asserts/logo/KeyboardArrowDown.svg";
import {
  fetchTestCases,
  generateTestCases,
  addTestCase,
  updateTestCase,
  deleteTestCase,
} from "../../services/testCaseClient";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import IntakeHeaderBackButton from "../../components/intake/IntakeHeaderBackButton";
import { STICKY_PAGE_HEADER_TEXT_COLUMN_SX } from "../../components/layout/stickyPageHeaderTokens";
import { GATE_SUBTABS_PL, GATE_SUBTABS_PR, GATE_SUBTABS_GAP } from "../../components/layout/layoutConstants";
import IntakeSuccessToast from "../../components/intake/IntakeSuccessToast";
import SelectDropdownTheme from "../../components/SelectDropdownTheme";
import AddTestCaseDialog from "./AddTestCaseDialog";
import DeleteTestCaseDialog from "./DeleteTestCaseDialog";
import SuspendTestCaseDialog from "./SuspendTestCaseDialog";
import ConfirmGroundTruthDialog, {
  formatOtherMetricGroups,
  sourceBreakdownForCases,
} from "./ConfirmGroundTruthDialog";
import ConfirmGroundTruthRowDialog from "./ConfirmGroundTruthRowDialog";
import ReviewQueueView from "./ReviewQueueView";
import AiProvenanceLink from "../../components/AiProvenanceLink";

const log = (...args) => console.log("[GATE1.TEST-CASES]", ...args);

const DOC_TABS = [
  { label: "Test Strategies", Icon: TestStrategyIcon },
  { label: "Test Cases", Icon: TestCasesIcon },
  { label: "Test Suite", Icon: TestSuiteIcon },
];
const ACTIVE_GATE = 1;
const TAB_ACTIVE = "#555555";
const TAB_INACTIVE = "#555555";
const TAB_UNDERLINE = "#005BF0";

/**
 * Back-arrow placement — same 24px / 16px ink standard as Gate 0 and Gate 1
 * headers. CSS backs out the 6px hit-target inset and Lato's ~1px capital
 * side bearing. Other header gaps stay 8px.
 */
const BACK_ARROW_INK_LEFT_PX = 24;
const BACK_ARROW_INK_GAP_RIGHT_PX = 16;
const BACK_ARROW_HIT_TARGET_INSET_PX = 6;
const TITLE_LEFT_SIDE_BEARING_PX = 1;
const HEADER_INSET_LEFT_PX = BACK_ARROW_INK_LEFT_PX - BACK_ARROW_HIT_TARGET_INSET_PX;
const BACK_ARROW_GAP_RIGHT_PX =
  BACK_ARROW_INK_GAP_RIGHT_PX - BACK_ARROW_HIT_TARGET_INSET_PX - TITLE_LEFT_SIDE_BEARING_PX;
const HEADER_ROW_GAP_PX = 8;
const TITLE_NUDGE_LEFT_PX = BACK_ARROW_GAP_RIGHT_PX - HEADER_ROW_GAP_PX;



/** Sidebar filter rows — `attr` is the backend trust_attribute value; `label` is what a person sees. */
const SIDEBAR_TRUST_ITEMS = TRUST_ATTRIBUTES.map((attr) => ({
  id: attr.toLowerCase(),
  attr,
  label: trustAttributeLabel(attr),
}));

const SIDEBAR_PANEL_PX = 2;

const SIDEBAR_SECTION_HEADER_SX = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  py: 1.25,
  px: SIDEBAR_PANEL_PX,
  cursor: "pointer",
  userSelect: "none",
};

const SIDEBAR_SECTION_HEADER_LEFT_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  minWidth: 0,
  flex: 1,
};

/** Right column — flush with panel edge (matches Filters close icon inset). */
const SIDEBAR_SECTION_HEADER_RIGHT_SX = {
  flexShrink: 0,
  ml: "auto",
  textAlign: "right",
};

/** Same primary borderless states as Test Strategy (hover underline, focus ring, active fill). */
const PRIMARY_BORDERLESS_SX = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#005BF0",
  height: 32,
  minHeight: 32,
  boxSizing: "border-box",
  px: "16px",
  py: "4px",
  gap: "4px",
  borderRadius: "4px",
  border: "2px solid transparent",
  bgcolor: "transparent",
  backgroundColor: "transparent",
  backgroundImage: "none",
  boxShadow: "none",
  verticalAlign: "middle",
  "&:hover:not(.Mui-disabled):not(:disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: "none",
    border: "2px solid transparent",
    color: "#005BF0",
    textDecoration: "underline",
    boxShadow: "none",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: "none !important",
    border: "2px solid #005FCC",
    color: "#005BF0",
    textDecoration: "none",
    boxShadow: "none",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: "none !important",
    border: "2px solid #005FCC",
    color: "#005BF0",
    textDecoration: "none",
  },
  "&:active:not(.Mui-disabled):not(:disabled)": {
    bgcolor: "#F0F6FF !important",
    backgroundColor: "#F0F6FF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    textDecoration: "none",
    boxShadow: "none",
  },
  "&.Mui-disabled, &:disabled": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: "none",
    border: "2px solid transparent",
    color: "#BBBBBB",
    textDecoration: "none",
    boxShadow: "none",
    cursor: "not-allowed",
  },
  "& .MuiTouchRipple-root": { display: "none" },
};

const SIDEBAR_SELECT_ALL_SX = {
  ...PRIMARY_BORDERLESS_SX,
  minWidth: 0,
  width: "fit-content",
  flexShrink: 0,
  textAlign: "right",
  justifyContent: "flex-end",
};

const SIDEBAR_FILTERS_CLOSE_BTN_SX = {
  p: 0,
  width: 18,
  height: 18,
  minWidth: 18,
  minHeight: 18,
  flexShrink: 0,
  color: "#667085",
  "&.MuiIconButton-sizeSmall": { p: 0 },
  "&:hover": { bgcolor: "transparent", color: "#344054" },
};

/** Sidebar filter rows — neutral background; selection shown via checkbox only */
const SIDEBAR_FILTER_ITEM_SX = {
  display: "flex",
  alignItems: "flex-start",
  gap: "15px",
  py: 1,
  pl: SIDEBAR_PANEL_PX,
  pr: 2,
  cursor: "pointer",
  borderTop: "1px solid #F2F4F7",
  bgcolor: "transparent",
  "&:hover": { bgcolor: "#FAFAFA" },
};

/** Sidebar filters compare register UUIDs from API rows — normalize for stable matching. */
function filterIdEquals(a, b) {
  return a != null && b != null && String(a) === String(b);
}

function filterIdInList(ids, id) {
  return ids.some((entry) => filterIdEquals(entry, id));
}

/** Cases in the on-screen metric group that still need ground-truth confirm. */
function pendingGroundTruthInGroup(group) {
  return (group?.testCases || []).filter(
    (tc) => tc.status !== "deactivated" && tc.status !== "verified"
  );
}

function countUniqueByStatus(cases, status) {
  const ids = new Set();
  for (const tc of cases || []) {
    if (tc.status === status && tc.id != null) ids.add(String(tc.id));
  }
  return ids.size;
}

const SIDEBAR_FILTER_CHECKBOX_PROPS = {
  sx: { mt: -0.1, flexShrink: 0 },
  tabIndex: -1,
  onClick: (e) => e.stopPropagation(),
};

const SIDEBAR_RISK_NAME_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "18px",
  color: "#383838",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
};

const SIDEBAR_RISK_META_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 11,
  fontWeight: 400,
  lineHeight: "16px",
  color: "#707070",
  mt: 0.25,
};

const COLLAPSED_FILTER_RAIL_WIDTH = 48;
const COLLAPSED_RAIL_CELL_HEIGHT = 64;

const COLLAPSED_RAIL_CELL_SX = {
  width: COLLAPSED_FILTER_RAIL_WIDTH,
  height: COLLAPSED_RAIL_CELL_HEIGHT,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  pt: 2,
  pr: 1.5,
  pb: 2,
  pl: 1.5,
  border: "1px solid #E6E6E6",
  bgcolor: "#FFFFFF",
  transition: "background-color 0.15s ease",
  "&:hover": { bgcolor: "#FAFAFA" },
  "&:not(:first-of-type)": {
    marginTop: { xs: 0, md: "-1px" },
    marginLeft: { xs: "-1px", md: 0 },
  },
};

const COLLAPSED_RAIL_COUNT_BADGE_SX = {
  position: "absolute",
  top: -7,
  right: -9,
  minWidth: 16,
  height: 16,
  px: 0.375,
  borderRadius: "999px",
  bgcolor: "#555555",
  color: "#FFFFFF",
  fontFamily: "Lato, sans-serif",
  fontSize: 10,
  fontWeight: 700,
  lineHeight: "16px",
  textAlign: "center",
};

const SIDEBAR_SECTION_COUNT_SX = {
  ...SIDEBAR_RISK_META_SX,
  mt: 0,
  flexShrink: 0,
  whiteSpace: "nowrap",
  textAlign: "right",
};

const STATUS_LABEL = {
  not_reviewed: "Not reviewed",
  verified: "Verified",
  flagged: "Flagged",
  deactivated: "Deactivated",
};

const STATUS_SX = {
  not_reviewed: { bgcolor: "#FEF3C7", color: "#B45309" },
  verified: { bgcolor: "#DCFCE7", color: "#15803D" },
  flagged: { bgcolor: "#FFEDD5", color: "#C2410C" },
  deactivated: { bgcolor: "#F5F5F5", color: "#707070" },
};

/** Shared Gate 1 button tokens — ghost primary, solid primary, ghost secondary pill, bulk text actions */
const BTN_SHADOW = "0px 1px 2px 0px #0000000A";
const BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_ACTIVE =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

const BTN_TEXT_BASE = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  boxSizing: "border-box",
  "& .MuiTouchRipple-root": { display: "none" },
};

/** Figma Ghost Primary Medium — hover #F0F6FF; focus stays white with 2px blue border */
const GHOST_PRIMARY_BTN_SX = {
  ...BTN_TEXT_BASE,
  fontSize: 13,
  lineHeight: "24px",
  height: 32,
  minHeight: 32,
  py: "4px",
  px: "16px",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  border: "1px solid #005BF0",
  color: "#005BF0",
  borderRadius: "4px",
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    bgcolor: "#FFFFFF",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E6E6E6",
    color: "#98A2B3",
  },
};

const PAGE_PRIMARY_BTN_SX = {
  ...BTN_TEXT_BASE,
  fontSize: 13,
  lineHeight: "24px",
  height: 32,
  minHeight: 32,
  py: "4px",
  px: "16px",
  gap: "4px",
  borderRadius: "4px",
  color: "#FFFFFF",
  bgcolor: "transparent",
  backgroundColor: "transparent",
  backgroundImage: PRIMARY_BG,
  border: "1px solid transparent",
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: PRIMARY_BG_HOVER,
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG_ACTIVE} !important`,
    border: "1px solid transparent",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    boxShadow: BTN_SHADOW,
    opacity: 1,
    cursor: "not-allowed",
  },
};

const PAGE_PRIMARY_BTN_MD_SX = {
  ...PAGE_PRIMARY_BTN_SX,
  height: 40,
  minHeight: 40,
  py: "8px",
  fontSize: 15,
};

/** Same footer chrome as Test Suite: 56px bar, ghost Back, primary action. */
const FOOTER_SX = {
  flexShrink: 0,
  height: 56,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: "24px",
  bgcolor: "#FFFFFF",
  boxShadow: "0px -1px 8px 0px #2122260A",
};

const FOOTER_GHOST_BTN_SX = {
  ...BTN_TEXT_BASE,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  color: "#005BF0",
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    color: "#BBBBBB",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    cursor: "not-allowed",
  },
};

const GO_TO_TEST_SUITES_BTN_SX = {
  ...BTN_TEXT_BASE,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  width: "auto",
  minWidth: 160,
  height: 40,
  minHeight: 40,
  py: "8px",
  px: "16px",
  gap: "4px",
  borderRadius: "4px",
  color: "#FFFFFF",
  bgcolor: "transparent",
  backgroundColor: "transparent",
  backgroundImage: PRIMARY_BG,
  border: "1px solid transparent",
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  flexShrink: 0,
  transition: "none",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: PRIMARY_BG_HOVER,
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG_ACTIVE} !important`,
    border: "1px solid transparent",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    boxShadow: BTN_SHADOW,
    opacity: 1,
    cursor: "not-allowed",
  },
};

const PAGE_PRIMARY_SPLIT_MAIN_SX = {
  ...PAGE_PRIMARY_BTN_SX,
  flex: { xs: 1, sm: "0 1 auto" },
  borderRadius: 0,
  borderTopLeftRadius: "4px",
  borderBottomLeftRadius: "4px",
  boxShadow: "none",
};

const PAGE_PRIMARY_SPLIT_CARET_SX = {
  ...PAGE_PRIMARY_BTN_SX,
  minWidth: 32,
  width: 32,
  height: 32,
  minHeight: 32,
  p: "4px 8px",
  flexShrink: 0,
  borderRadius: 0,
  borderTopRightRadius: "4px",
  borderBottomRightRadius: "4px",
  borderLeft: "1px solid rgba(255, 255, 255, 0.35)",
  boxShadow: "none",
  "& svg path": { fill: "#FFFFFF", stroke: "#FFFFFF" },
};

/** Figma ghost secondary pill — load-more footer */
const GHOST_SECONDARY_PILL_BTN_SX = {
  ...BTN_TEXT_BASE,
  fontWeight: 600,
  fontSize: 13,
  lineHeight: "20px",
  color: "#555555",
  border: "1px solid #E6E6E6",
  borderRadius: "999px",
  px: 2.5,
  py: 0.75,
  minWidth: 0,
  width: "auto",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  boxShadow: "none",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#FAFAFA",
    backgroundColor: "#FAFAFA",
    borderColor: "#D0D5DD",
    boxShadow: "none",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    border: "2px solid #BBBBBB",
    color: "#555555",
    boxShadow: "none",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    border: "2px solid #BBBBBB",
    color: "#555555",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    border: "1px solid #D0D5DD",
    boxShadow: BTN_INSET,
  },
};

const BULK_WARNING_BTN_SX = {
  ...BTN_TEXT_BASE,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#8A5C00",
  minWidth: 0,
  px: 1,
  bgcolor: "transparent",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#FFF5E0",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "transparent !important",
    boxShadow: "inset 0 0 0 2px #FFDEA6",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    boxShadow: "inset 0 0 0 2px #FFDEA6",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFEBC7 !important",
    boxShadow: "none",
  },
  "&.Mui-disabled": { color: "#8A5C00", opacity: 0.4 },
  "& .MuiButton-startIcon": { mr: "8px", "& svg": { width: 21, height: 21 } },
};

const BULK_DANGER_BTN_SX = {
  ...BTN_TEXT_BASE,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#DE350B",
  minWidth: 0,
  px: 1,
  bgcolor: "transparent",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#FEF2F2",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "transparent !important",
    boxShadow: "inset 0 0 0 2px #FECACA",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    boxShadow: "inset 0 0 0 2px #FECACA",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FEE2E2 !important",
    boxShadow: "none",
  },
  "&.Mui-disabled": { color: "#DE350B", opacity: 0.4 },
  "& .MuiButton-startIcon": { mr: "8px", "& svg": { width: 21, height: 21 } },
};

const BULK_SUCCESS_BTN_SX = {
  ...BTN_TEXT_BASE,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#00754E",
  minWidth: 0,
  px: 1,
  bgcolor: "transparent",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#E8FCF3",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "transparent !important",
    boxShadow: "inset 0 0 0 2px #ABF5D1",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    boxShadow: "inset 0 0 0 2px #ABF5D1",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#DCFCE7 !important",
    boxShadow: "none",
  },
  "&.Mui-disabled": { color: "#00754E", opacity: 0.4 },
  "& .MuiButton-startIcon": { mr: "8px", "& svg": { width: 21, height: 21 } },
};

const ACTION_ICON_STYLE = { width: 18, height: 18, display: "block" };

/** Figma expand/collapse chevron — BlackFilledExpandIcon.svg (right → down when open) */
function SectionExpandChevron({ open, size = 15, style }) {
  return (
    <BlackFilledExpandIcon
      style={{
        width: size,
        height: size,
        display: "block",
        flexShrink: 0,
        transform: open ? "rotate(90deg)" : "none",
        transition: "transform 0.15s ease",
        ...style,
      }}
    />
  );
}

function isGeneratedLineageSource(source) {
  return source === "Generated" || source === "Modified";
}

function selectedRowsFrom(store, cases) {
  const rows = [];
  for (let i = 0; i < cases.length; i += 1) {
    if (store.has(cases[i].id)) rows.push(cases[i]);
  }
  return rows;
}

function isMixedSourceSelection(rows) {
  let custom = false;
  let lineage = false;
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i].source === "Custom") custom = true;
    else if (isGeneratedLineageSource(rows[i].source)) lineage = true;
    if (custom && lineage) return true;
  }
  return false;
}

/** Figma — row actions kebab menu (Confirm Ground Truth, Edit, Delete, Suspend / Reactivate) */
const TEST_CASE_ROW_ACTIONS_MENU_PAPER_SX = {
  borderRadius: "4px",
  border: "1px solid #E6E6E6",
  boxShadow: "0px 4px 12px rgba(16, 24, 40, 0.12)",
  minWidth: 196,
  mt: 0.5,
  py: 0.5,
};

/** Figma menu item — Lato Regular 14px / 24px line-height, Grey-60 (#555555) */
const TEST_CASE_ROW_MENU_ITEM_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: 14,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#555555",
  py: 1,
  px: 2,
  minHeight: 36,
  "&:hover": { bgcolor: "#F0F6FF", color: "#555555" },
  "&.Mui-disabled": { color: "#555555", opacity: 0.45 },
  "&.Mui-focusVisible": { bgcolor: "#F0F6FF", color: "#555555" },
};

function TestCaseRowActionsMenu({
  testCase,
  onEdit,
  onDeleteClick,
  onSuspendClick,
  onReactivateClick,
  onConfirmGroundTruthClick,
  allowDelete = true,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isDeactivated = testCase.status === "deactivated";
  const canConfirmGroundTruth = testCase.status === "not_reviewed";
  const canDelete = allowDelete && testCase.source === "Custom";
  const canLifecycle = isGeneratedLineageSource(testCase.source);

  const closeMenu = () => setAnchorEl(null);

  const handleTriggerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  return (
    <>
      <IconButton
        size="small"
        disableRipple
        aria-label="Test case actions"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleTriggerClick}
        sx={{ p: 0.75 }}
      >
        <ThreeDotsIcon style={{ width: 16, height: 4, display: "block" }} aria-hidden />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: TEST_CASE_ROW_ACTIONS_MENU_PAPER_SX }}
      >
        <MenuItem
          disabled={!canConfirmGroundTruth}
          onClick={() => {
            if (!canConfirmGroundTruth) return;
            closeMenu();
            onConfirmGroundTruthClick(testCase);
          }}
          sx={TEST_CASE_ROW_MENU_ITEM_SX}
        >
          Confirm Ground Truth
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            onEdit(testCase);
          }}
          sx={TEST_CASE_ROW_MENU_ITEM_SX}
        >
          Edit
        </MenuItem>
        <MenuItem
          disabled={!canDelete}
          onClick={() => {
            if (!canDelete) return;
            closeMenu();
            onDeleteClick(testCase);
          }}
          sx={TEST_CASE_ROW_MENU_ITEM_SX}
        >
          Delete
        </MenuItem>
        {canLifecycle ? (
        <MenuItem
          onClick={() => {
            closeMenu();
            if (isDeactivated) onReactivateClick(testCase);
            else onSuspendClick(testCase);
          }}
          sx={TEST_CASE_ROW_MENU_ITEM_SX}
        >
          {isDeactivated ? "Reactivate" : "Deactivate"}
        </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}

function sourceBadgeSx(source) {
  if (source === "Custom") return { bgcolor: "#FFEDD5", color: "#C2410C" };
  if (source === "Modified") return { bgcolor: "#E6E6E6", color: "#555555" };
  return { bgcolor: "#F5EEFF", color: "#7F2CE8" };
}

function sourceBadgeShape(source) {
  if (source === "Generated") return "12px";
  return "999px";
}

function sourceLabel(source) {
  if (source === "Custom") return "Custom";
  if (source === "Modified") return "Modified";
  return "Generated";
}

function promptPreview(tc) {
  const first = (tc.input || [])[0];
  if (typeof first === "string") return first;
  if (first && typeof first === "object") return first.text || first.prompt || first.content || "";
  return "";
}

/** Figma Tooltip — black pill, Lato 12/18, arrow on top (placement bottom). */
const FIGMA_TOOLTIP_SX = {
  bgcolor: "#000000",
  borderRadius: "4px",
  px: "12px",
  py: "12px",
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "18px",
  letterSpacing: 0,
  color: "#E6E6E6",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.25)",
  "& .MuiTooltip-arrow": { color: "#000000" },
};

const HOVER_ACTION_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      ...FIGMA_TOOLTIP_SX,
      whiteSpace: "nowrap",
    },
  },
};

function HoverActionTooltip({ title, placement = "bottom", children }) {
  return (
    <Tooltip title={title} arrow placement={placement} slotProps={HOVER_ACTION_TOOLTIP_SLOT_PROPS}>
      {children}
    </Tooltip>
  );
}

const TABLE_CHECKBOX_SX = {
  p: 0,
  "& .MuiSvgIcon-root": { fontSize: 18 },
};

const CHECKBOX_ICON_PROPS = { style: { width: 18, height: 18, display: "block" } };

function CheckBoxCheckedIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" fill="#005BF0" stroke="#005BF0" />
      <path
        d="M5.25 9L7.75 11.5L12.75 6.5"
        stroke="#FFFFFF"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckBoxIndeterminateIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" fill="#005BF0" stroke="#005BF0" />
      <path d="M5.25 9H12.75" stroke="#FFFFFF" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function TestCaseCheckbox({ sx, ...props }) {
  return (
    <Checkbox
      size="small"
      disableRipple
      icon={<CheckBoxUncheckedIcon {...CHECKBOX_ICON_PROPS} />}
      checkedIcon={<CheckBoxCheckedIcon {...CHECKBOX_ICON_PROPS} />}
      indeterminateIcon={<CheckBoxIndeterminateIcon {...CHECKBOX_ICON_PROPS} />}
      sx={{ ...TABLE_CHECKBOX_SX, ...sx }}
      {...props}
    />
  );
}

function SelectionActionBar({
  store,
  visibleRows,
  cases,
  statusTab,
  onToggleSelectAll,
  onDeactivate,
  onReactivate,
  onDelete,
}) {
  const isDeactivatedTab = statusTab === "deactivated";
  const isCustomTab = statusTab === "custom";
  const isGeneratedTab = statusTab === "generated";
  const visibleIds = visibleRows.map((tc) => tc.id);
  const selectedCount = useSyncExternalStore(store.subscribe, () => {
    let n = 0;
    for (let i = 0; i < cases.length; i += 1) {
      if (store.has(cases[i].id)) n += 1;
    }
    return n;
  });
  const pageState = useSelectionTriState(store, visibleIds);
  const actionFlags = useSyncExternalStore(store.subscribe, () => {
    let canDeactivate = false;
    let canDelete = false;
    let canReactivate = false;
    for (let i = 0; i < cases.length; i += 1) {
      const row = cases[i];
      if (!store.has(row.id)) continue;
      if (isGeneratedLineageSource(row.source) && row.status !== "deactivated") canDeactivate = true;
      if (row.source === "Custom") canDelete = true;
      if (isGeneratedLineageSource(row.source) && row.status === "deactivated") canReactivate = true;
      if (canDeactivate && canDelete && canReactivate) break;
    }
    return `${canDeactivate ? "1" : "0"}${canDelete ? "1" : "0"}${canReactivate ? "1" : "0"}`;
  });
  const allPageSelected = pageState === 2;
  const somePageSelected = pageState === 1;
  const canDeactivate = actionFlags.startsWith("1");
  const canDelete = actionFlags[1] === "1";
  const canReactivate = actionFlags.endsWith("1");

  if (selectedCount === 0) return null;

  return (
    <Box
      sx={{
        width: "100%",
        height: 64,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        bgcolor: "#FFFFFF",
        borderBottom: "1px solid #E6E6E6",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: `${TEST_CASE_CHECKBOX_TO_NAME_GAP}px`,
          pl: `${TEST_CASE_CHECKBOX_COL_INSET}px`,
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <TestCaseCheckbox
          checked={allPageSelected}
          indeterminate={somePageSelected || (!allPageSelected && selectedCount > 0)}
          onChange={onToggleSelectAll}
        />
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 500,
            fontSize: 14,
            lineHeight: "21px",
            letterSpacing: 0,
            color: "#364153",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {selectedCount} selected
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0, ml: "auto", pr: 2 }}>
        {isDeactivatedTab ? (
          <Button
            disableRipple
            onClick={onReactivate}
            disabled={!canReactivate}
            startIcon={<PlayCircleIcon style={{ width: 21, height: 21, display: "block", opacity: 1 }} />}
            sx={BULK_SUCCESS_BTN_SX}
          >
            Reactivate
          </Button>
        ) : isCustomTab ? (
          <Button
            disableRipple
            onClick={onDelete}
            disabled={!canDelete}
            startIcon={<DeleteIcon style={{ width: 21, height: 21, display: "block", opacity: 1 }} />}
            sx={BULK_DANGER_BTN_SX}
          >
            Delete
          </Button>
        ) : isGeneratedTab ? (
          <>
            {canDeactivate ? (
              <Button
                disableRipple
                onClick={onDeactivate}
                startIcon={<PauseCircleIcon style={{ width: 21, height: 21, display: "block", opacity: 1 }} />}
                sx={BULK_WARNING_BTN_SX}
              >
                Deactivate
              </Button>
            ) : null}
            {canReactivate ? (
              <Button
                disableRipple
                onClick={onReactivate}
                startIcon={<PlayCircleIcon style={{ width: 21, height: 21, display: "block", opacity: 1 }} />}
                sx={BULK_SUCCESS_BTN_SX}
              >
                Reactivate
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <Button
              disableRipple
              onClick={onDeactivate}
              disabled={!canDeactivate}
              startIcon={<PauseCircleIcon style={{ width: 21, height: 21, display: "block", opacity: 1 }} />}
              sx={BULK_WARNING_BTN_SX}
            >
              Deactivate
            </Button>
            <Button
              disableRipple
              onClick={onDelete}
              disabled={!canDelete}
              startIcon={<DeleteIcon style={{ width: 21, height: 21, display: "block", opacity: 1 }} />}
              sx={BULK_DANGER_BTN_SX}
            >
              Delete
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

function MixedSourceSelectionDialog({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "4px",
          boxSizing: "border-box",
          width: { xs: "calc(100% - 32px)", sm: 480 },
          maxWidth: 480,
          mx: 2,
          pt: "40px",
          pr: { xs: 3, sm: "64px" },
          pb: "40px",
          pl: { xs: 3, sm: "64px" },
          position: "relative",
          boxShadow:
            "0px 0px 10px 0px rgba(33, 34, 38, 0.12), 0px 10px 10px 0px rgba(0, 0, 0, 0.24)",
        },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: "rgba(52, 64, 84, 0.45)" } },
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        sx={{ position: "absolute", top: 8, right: 8, color: "#555555" }}
      >
        <CloseIcon sx={{ fontSize: 24 }} />
      </IconButton>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", width: "100%" }}>
        <Box
          aria-hidden
          sx={{
            width: 64,
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 43, color: "#555555" }} />
        </Box>
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 900,
            fontSize: 20,
            lineHeight: "27px",
            color: "#000000",
            textAlign: "center",
          }}
        >
          Mixed test case types selected
        </Typography>
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 400,
            fontSize: 14,
            lineHeight: "21px",
            color: "#555555",
            textAlign: "center",
          }}
        >
          The selected test cases support different actions.
        </Typography>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.5,
            fontFamily: "Lato, sans-serif",
            fontWeight: 400,
            fontSize: 14,
            lineHeight: "21px",
            color: "#555555",
            textAlign: "left",
            width: "100%",
          }}
        >
          <li>Custom test cases can only be deleted.</li>
          <li>Generated and Modified test cases can only be deactivated or reactivated.</li>
        </Box>
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 400,
            fontSize: 14,
            lineHeight: "21px",
            color: "#555555",
            textAlign: "center",
          }}
        >
          Please select only one test case type and try again.
        </Typography>
        <Button
          variant="outlined"
          disableRipple
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontFamily: "Lato, sans-serif",
            fontWeight: 700,
            fontSize: 14,
            lineHeight: "20px",
            color: "#555555",
            borderColor: "#BBBBBB",
            borderRadius: "4px",
            px: "16px",
            height: 36,
            minHeight: 36,
            py: 0,
            bgcolor: "#F5F5F5",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#EEEEEE",
              borderColor: "#BBBBBB",
              boxShadow: "none",
            },
          }}
        >
          OK
        </Button>
      </Box>
    </Dialog>
  );
}

function TwoLineCell({ primary, secondary, primarySx, secondarySx }) {
  return (
    <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
      <TruncatedCellLine
        text={primary || "—"}
        sx={{
          ...TEST_CASE_NAME_VALUE_SX,
          ...primarySx,
        }}
      />
      {secondary ? (
        <TruncatedCellLine
          text={secondary}
          sx={{ mt: 0.25, ...TEST_CASE_TRUST_ATTRIBUTE_SX, ...secondarySx }}
        />
      ) : null}
    </Box>
  );
}

/** Figma — metric name (accordion header / metric column): Lato Bold 16px, Grey-70 (#383838) */
const METRIC_NAME_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "16px",
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#383838",
};

/** Figma — metric accordion header card + typography */
const METRIC_HEADER_NAME_SX = {
  ...METRIC_NAME_SX,
  flexShrink: 0,
};

const METRIC_HEADER_COUNT_BOLD_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: 14,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#555555",
};

const METRIC_HEADER_COUNT_REGULAR_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: 14,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#555555",
};

const METRIC_HEADER_CONFIRMED_SX = {
  fontFamily: "Lato, sans-serif",
  fontStyle: "normal",
  fontSize: 12,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#555555",
  whiteSpace: "nowrap",
};

function MetricAccordionHeader({
  metricName,
  count,
  verifiedCount,
  /** Denominator for “confirmed” (defaults to count); use active non-deactivated cases when set. */
  confirmedTotal,
  expanded,
  onToggle,
  onConfirmGroundTruth,
  confirmBusy = false,
  confirmGroundTruthDisabled = false,
  disableBorderBottom = false,
}) {
  const confirmedDenom = confirmedTotal ?? count;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        alignItems: { xs: "flex-start", lg: "center" },
        gap: { xs: 1, lg: 2 },
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        px: "16px",
        py: "12px",
        bgcolor: "#FFFFFF",
        ...(disableBorderBottom ? {} : { borderBottom: "1px solid #E6E6E6" }),
        cursor: "pointer",
        "&:hover": { bgcolor: "#FAFAFA" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flex: 1 }}>
        <SectionExpandChevron open={expanded} size={15} />
        <Typography component="span" sx={METRIC_HEADER_NAME_SX}>
          {metricName || "Unlinked metric"}
        </Typography>
        <Typography component="span" sx={{ flexShrink: 0, display: "inline-flex", alignItems: "baseline", gap: "4px" }}>
          <Box component="span" sx={METRIC_HEADER_COUNT_BOLD_SX}>
            {count}
          </Box>
          <Box component="span" sx={METRIC_HEADER_COUNT_REGULAR_SX}>
            {count === 1 ? "Test case" : "Test cases"}
          </Box>
        </Typography>
      </Box>
      {onConfirmGroundTruth ? (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
          flexShrink: 0,
          width: { xs: "100%", lg: "auto" },
          pl: { xs: 3.5, lg: 0 },
        }}
      >
        <Typography sx={METRIC_HEADER_CONFIRMED_SX}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {verifiedCount}
          </Box>
          <Box component="span" sx={{ fontWeight: 400 }}>
            {" "}
            of{" "}
          </Box>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {confirmedDenom}
          </Box>
          <Box component="span" sx={{ fontWeight: 400 }}>
            {" "}
            confirmed
          </Box>
        </Typography>
        <Button
          variant="outlined"
          disableElevation
          disableRipple
          disabled={confirmBusy || confirmGroundTruthDisabled}
          onClick={(e) => {
            e.stopPropagation();
            if (confirmBusy || confirmGroundTruthDisabled) return;
            onConfirmGroundTruth();
          }}
          sx={GHOST_PRIMARY_BTN_SX}
        >
          {confirmBusy ? "Confirming…" : "Confirm ground truth"}
        </Button>
      </Box>
      ) : null}
    </Box>
  );
}

function MetricLoadMoreFooter({ shown, total, onLoadMore, onShowLess }) {
  const remaining = Math.max(0, total - shown);
  const canShowLess = shown > METRIC_INITIAL_VISIBLE;
  if (remaining <= 0 && !canShowLess) return null;

  const isShowLess = remaining <= 0;
  const loadCount = Math.min(METRIC_LOAD_MORE_STEP, remaining);

  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        pt: "16px",
        px: 0,
        bgcolor: "transparent",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Lato, sans-serif",
          fontSize: 12,
          lineHeight: "14px",
          letterSpacing: 0,
          fontWeight: 400,
          fontStyle: "normal",
          color: "#555555",
        }}
      >
        Showing{" "}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {shown}
        </Box>
        {" "}of{" "}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {total}
        </Box>
        {" "}test cases
      </Typography>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: 42,
          mt: "8px",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            borderTop: "1px solid #E6E6E6",
            pointerEvents: "none",
          }}
        />
        <Button
          variant="outlined"
          disableElevation
          disableRipple
          onClick={isShowLess ? onShowLess : onLoadMore}
          endIcon={<KeyboardArrowDownIcon style={{ width: 16, height: 16 }} />}
          sx={{
            position: "relative",
            zIndex: 1,
            ...GHOST_SECONDARY_PILL_BTN_SX,
          }}
        >
          {isShowLess ? "Show less" : `Load ${loadCount} more`}
        </Button>
      </Box>
    </Box>
  );
}

function TestCaseTableRow({
  testCase,
  store,
  onToggleSelect,
  onView,
  onEdit,
  onDeleteClick,
  onSuspendClick,
  onReactivate,
  onConfirmGroundTruthClick,
  showMetricColumn = false,
  allowDelete = true,
}) {
  const selected = useSelectionHas(store, testCase.id);
  const activateView = onView
    ? (event) => {
        if (event?.defaultPrevented) return;
        onView(testCase);
      }
    : undefined;
  const dataCellClickProps = activateView
    ? { onClick: activateView, sx: { cursor: "pointer" } }
    : {};

  return (
    <TableRow
      hover
      selected={selected}
      onClick={activateView}
      role={activateView ? "button" : undefined}
      tabIndex={activateView ? 0 : undefined}
      aria-label={activateView ? `View test case ${testCase.name || ""}`.trim() : undefined}
      onKeyDown={
        activateView
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onView(testCase);
              }
            }
          : undefined
      }
      sx={{
        ...TABLE_ROW_SX,
        ...(activateView ? { cursor: "pointer" } : {}),
      }}
    >
      <TableCell sx={TABLE_STICKY_CHECKBOX_BODY_SX} onClick={(e) => e.stopPropagation()}>
        <TestCaseCheckbox
          checked={selected}
          onChange={() => onToggleSelect(testCase.id)}
        />
      </TableCell>
      <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...TABLE_NAME_CELL_SX, ...tableColSx("name"), ...dataCellClickProps.sx }} onClick={dataCellClickProps.onClick}>
        <TwoLineCell primary={testCase.name} secondary={testCase.trustAttribute ? trustAttributeLabel(testCase.trustAttribute) : null} />
      </TableCell>
      <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...tableColSx("risk", "sm"), ...dataCellClickProps.sx }} onClick={dataCellClickProps.onClick}>
        <TruncatedCellLine text={testCase.riskName || "—"} sx={TEST_CASE_RISK_NAME_SX} />
      </TableCell>
      {showMetricColumn ? (
        <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...tableColSx("metric", "sm"), ...dataCellClickProps.sx }} onClick={dataCellClickProps.onClick}>
          <TwoLineCell
            primary={testCase.metricName}
            secondary={testCase.metricCode || null}
            primarySx={METRIC_NAME_SX}
          />
        </TableCell>
      ) : null}
      <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...tableColSx("type"), whiteSpace: "nowrap", ...dataCellClickProps.sx }} onClick={dataCellClickProps.onClick}>
        <Chip
          label={sourceLabel(testCase.source)}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: testCase.source === "Generated" ? 13 : 12,
            height: 24,
            borderRadius: sourceBadgeShape(testCase.source),
            px: 1,
            flexShrink: 0,
            ...sourceBadgeSx(testCase.source),
          }}
        />
      </TableCell>
      <TableCell sx={{ ...TABLE_BODY_CELL_SX, ...tableColSx("status"), whiteSpace: "nowrap", ...dataCellClickProps.sx }} onClick={dataCellClickProps.onClick}>
        <Chip
          label={STATUS_LABEL[testCase.status] || testCase.status}
          size="small"
          sx={{
            fontWeight: testCase.status === "not_reviewed" ? 600 : 700,
            fontSize: 12,
            height: 24,
            borderRadius: "999px",
            px: 1.5,
            flexShrink: 0,
            ...(STATUS_SX[testCase.status] || {}),
          }}
        />
      </TableCell>
      <TableCell align="center" sx={TABLE_STICKY_ACTIONS_BODY_SX} onClick={(e) => e.stopPropagation()}>
        <TestCaseRowActionsMenu
          testCase={testCase}
          onEdit={onEdit}
          onDeleteClick={onDeleteClick}
          onSuspendClick={onSuspendClick}
          onReactivateClick={onReactivate}
          onConfirmGroundTruthClick={onConfirmGroundTruthClick}
          allowDelete={allowDelete}
        />
      </TableCell>
    </TableRow>
  );
}

function MetricTestCasesTable({
  testCases,
  store,
  onToggleSelect,
  onToggleSelectSection,
  onView,
  onEdit,
  onDeleteClick,
  onSuspendClick,
  onReactivate,
  onConfirmGroundTruthClick,
  allowDelete = true,
}) {
  const colSpan = TABLE_COLUMNS_GROUPED.length + 1;
  const sectionIds = testCases.map((tc) => tc.id);
  const sectionState = useSelectionTriState(store, sectionIds);
  const allSectionSelected = sectionState === 2;
  const someSectionSelected = sectionState === 1;

  return (
    <Box sx={{ bgcolor: "#FFFFFF" }}>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: TEST_CASES_VIEW_MIN_WIDTH,
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <colgroup>
            <col style={{ width: TEST_CASE_COL_PX.checkbox }} />
            <col />
            <col style={{ width: TEST_CASE_COL_PX.risk }} />
            <col style={{ width: TEST_CASE_COL_PX.type }} />
            <col style={{ width: TEST_CASE_COL_PX.status }} />
            <col style={{ width: TEST_CASE_COL_PX.actions }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={TABLE_STICKY_CHECKBOX_HEAD_SX}>
                <TestCaseCheckbox
                  checked={allSectionSelected}
                  indeterminate={someSectionSelected}
                  onChange={onToggleSelectSection}
                />
              </TableCell>
              {TABLE_COLUMNS_GROUPED.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{
                    ...TABLE_HEAD_CELL_SX,
                    ...(col.key === "name" ? TABLE_NAME_CELL_SX : {}),
                    ...tableColSx(col.key, col.hideBelow),
                    ...(col.key === "actions" ? TABLE_STICKY_ACTIONS_HEAD_SX : {}),
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {testCases.map((tc) => (
              <TestCaseTableRow
                key={tc.id}
                testCase={tc}
                store={store}
                onToggleSelect={onToggleSelect}
                onView={onView}
                onEdit={onEdit}
                onDeleteClick={onDeleteClick}
                onSuspendClick={onSuspendClick}
                onReactivate={onReactivate}
                onConfirmGroundTruthClick={onConfirmGroundTruthClick}
                allowDelete={allowDelete}
              />
            ))}
            {!testCases.length && (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 3, color: "#98A2B3", fontSize: 13 }}>
                  No test cases in this metric.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const TABLE_COLUMNS = [
  { key: "name", label: "Test Case Name", hideBelow: null },
  { key: "risk", label: "Risk", hideBelow: "sm" },
  { key: "metric", label: "Metric", hideBelow: "sm" },
  { key: "type", label: "Type", hideBelow: null },
  { key: "status", label: "Status", hideBelow: null },
  { key: "actions", label: "Actions", hideBelow: null, align: "center" },
];

/** Metric accordion tables omit the Metric column — context is on the section header. */
const TABLE_COLUMNS_GROUPED = TABLE_COLUMNS.filter((col) => col.key !== "metric");

const METRIC_INITIAL_VISIBLE = 5;
const METRIC_LOAD_MORE_STEP = 25;

/** Hide toolbar Review Queue; metric-level Confirm ground truth remains active. */
const SHOW_REVIEW_QUEUE_BUTTON = false;

/** Figma — row inset, 18px checkbox, 12px gap before test case name */
const TEST_CASE_CHECKBOX_COL_INSET = 16;
const TEST_CASE_CHECKBOX_TO_NAME_GAP = 20;
const TEST_CASE_CHECKBOX_ICON_PX = 18;

/** Fixed column widths — prevents reflow when page size / row content changes */
const TEST_CASE_COL_PX = {
  checkbox: TEST_CASE_CHECKBOX_COL_INSET + TEST_CASE_CHECKBOX_ICON_PX,
  risk: 160,
  type: 120,
  status: 148,
  actions: 88,
};

const TEST_CASES_TABLE_SCROLL_SX = {
  flex: 1,
  minWidth: 0,
  px: { xs: 2, md: 3 },
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

/** Sum of fixed px columns + flex-column minimums for horizontal scroll threshold */
function testCasesViewMinWidth({ riskMetric, nameMin }) {
  let sum =
    TEST_CASE_COL_PX.checkbox +
    TEST_CASE_COL_PX.type +
    TEST_CASE_COL_PX.status +
    TEST_CASE_COL_PX.actions +
    nameMin;
  if (riskMetric) sum += TEST_CASE_COL_PX.risk;
  return sum;
}

/** Responsive min-width — table + list share this so both views scroll consistently */
const TEST_CASES_VIEW_MIN_WIDTH = {
  xs: testCasesViewMinWidth({ riskMetric: false, nameMin: 120 }),
  sm: testCasesViewMinWidth({ riskMetric: true, nameMin: 140 }),
  md: testCasesViewMinWidth({ riskMetric: true, nameMin: 160 }),
};

const TEST_CASES_VIEW_INNER_SX = {
  width: "100%",
  minWidth: TEST_CASES_VIEW_MIN_WIDTH,
  boxSizing: "border-box",
};

const TABLE_COL_WIDTH_SX = {
  checkbox: { width: TEST_CASE_COL_PX.checkbox, minWidth: TEST_CASE_COL_PX.checkbox, maxWidth: TEST_CASE_COL_PX.checkbox },
  name: { minWidth: { xs: 120, sm: 140, md: 160 } },
  risk: {
    width: TEST_CASE_COL_PX.risk,
    minWidth: TEST_CASE_COL_PX.risk,
    maxWidth: TEST_CASE_COL_PX.risk,
  },
  metric: { minWidth: 120 },
  type: { width: TEST_CASE_COL_PX.type, minWidth: TEST_CASE_COL_PX.type, maxWidth: TEST_CASE_COL_PX.type },
  status: { width: TEST_CASE_COL_PX.status, minWidth: TEST_CASE_COL_PX.status, maxWidth: TEST_CASE_COL_PX.status },
  actions: { width: TEST_CASE_COL_PX.actions, minWidth: TEST_CASE_COL_PX.actions, maxWidth: TEST_CASE_COL_PX.actions },
};

const TABLE_NAME_CELL_SX = {
  pl: `${TEST_CASE_CHECKBOX_TO_NAME_GAP}px`,
  pr: 2,
};

const TEST_CASES_VIEW_SCROLL_SX = {
  flex: 1,
  overflow: "auto",
  px: { xs: 2, md: 3 },
  minWidth: 0,
  scrollbarGutter: "stable",
};

function createSelectionStore() {
  let ids = new Set();
  const listeners = new Set();
  const emit = () => {
    listeners.forEach((listener) => listener());
  };
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    has(id) {
      return ids.has(id);
    },
    size() {
      return ids.size;
    },
    getIds() {
      return ids;
    },
    toggle(id) {
      const next = new Set(ids);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      ids = next;
      emit();
    },
    toggleAll(idList, select) {
      const next = new Set(ids);
      if (select) idList.forEach((id) => next.add(id));
      else idList.forEach((id) => next.delete(id));
      ids = next;
      emit();
    },
    clear() {
      if (ids.size === 0) return;
      ids = new Set();
      emit();
    },
  };
}

function useSelectionHas(store, id) {
  return useSyncExternalStore(store.subscribe, () => store.has(id));
}

function useSelectionTriState(store, ids) {
  return useSyncExternalStore(store.subscribe, () => {
    if (!ids.length) return 0;
    let hit = 0;
    for (let i = 0; i < ids.length; i += 1) {
      if (store.has(ids[i])) hit += 1;
    }
    if (hit === 0) return 0;
    if (hit === ids.length) return 2;
    return 1;
  });
}

/** Figma — column headings (table + list): Lato Bold 12px, Grey-70 (#383838) */
const TEST_CASE_COLUMN_HEADING_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: 12,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#383838",
};

/** Figma — test case name cell (primary line): Lato SemiBold 12px, Grey-60 */
const TEST_CASE_NAME_VALUE_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 600,
  fontStyle: "normal",
  fontSize: 12,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#555555",
};

/** Figma — trust attribute sub-line under test case name: Lato Regular 12px, Grey-50 */
const TEST_CASE_TRUST_ATTRIBUTE_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: 12,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#707070",
};

/** Figma — risk name cell: Lato SemiBold 12px, Grey-60 */
const TEST_CASE_RISK_NAME_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 600,
  fontStyle: "normal",
  fontSize: 12,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#555555",
};

const TABLE_HEAD_CELL_SX = {
  ...TEST_CASE_COLUMN_HEADING_SX,
  bgcolor: "#FFFFFF",
  borderBottom: "1px solid #E6E6E6",
  whiteSpace: "nowrap",
  height: 40,
  minHeight: 40,
  py: 0,
  px: 2,
  boxSizing: "border-box",
  verticalAlign: "middle",
};

const TABLE_BODY_CELL_SX = {
  borderBottom: "1px solid var(--Grey-20, #E6E6E6)",
  py: 0,
  px: 2,
  height: 64,
  minHeight: 64,
  boxSizing: "border-box",
  verticalAlign: "middle",
};

/** Figma 4577:24127 — table row hover: Grey-10 fill, Grey-20 dividers (no link underline) */
const TABLE_GREY_10 = "#F5F5F5";
const TABLE_GREY_20 = "#E6E6E6";
const TABLE_ROW_SELECTED_BG = "#F8FAFF";
const TABLE_ROW_SELECTED_HOVER_BG = "#F0F6FF";

const TABLE_ROW_SX = {
  "&:last-child td": { borderBottom: `1px solid ${TABLE_GREY_20}` },
  transition: "background-color 0.15s ease",
  "&.Mui-selected": { bgcolor: TABLE_ROW_SELECTED_BG },
  "&.Mui-selected:hover": { bgcolor: TABLE_ROW_SELECTED_HOVER_BG },
  "&.MuiTableRow-hover:hover": { bgcolor: TABLE_GREY_10 },
  "&:hover": { bgcolor: TABLE_GREY_10 },
};

const TABLE_STICKY_CHECKBOX_BG = {
  bgcolor: "#FFFFFF",
  transition: "background-color 0.15s ease",
  ".MuiTableRow-root:hover &": { bgcolor: TABLE_GREY_10 },
  ".MuiTableRow-root.Mui-selected &": { bgcolor: TABLE_ROW_SELECTED_BG },
  ".MuiTableRow-root.Mui-selected:hover &": { bgcolor: TABLE_ROW_SELECTED_HOVER_BG },
};

const TABLE_STICKY_CHECKBOX_HEAD_SX = {
  ...TABLE_HEAD_CELL_SX,
  position: "sticky",
  left: 0,
  zIndex: 10,
  width: TEST_CASE_COL_PX.checkbox,
  minWidth: TEST_CASE_COL_PX.checkbox,
  maxWidth: TEST_CASE_COL_PX.checkbox,
  px: 0,
  pl: `${TEST_CASE_CHECKBOX_COL_INSET}px`,
  pr: 0,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
};

const TABLE_STICKY_ID_HEAD_SX = {
  ...TABLE_HEAD_CELL_SX,
  position: "sticky",
  left: TEST_CASE_COL_PX.checkbox,
  zIndex: 10,
  width: TEST_CASE_COL_PX.id,
  minWidth: TEST_CASE_COL_PX.id,
  maxWidth: TEST_CASE_COL_PX.id,
  boxSizing: "border-box",
  boxShadow: "2px 0 6px rgba(16, 24, 40, 0.08)",
};

const TABLE_STICKY_ACTIONS_HEAD_SX = {
  ...TABLE_HEAD_CELL_SX,
  position: "sticky",
  right: 0,
  zIndex: 10,
  width: TEST_CASE_COL_PX.actions,
  minWidth: TEST_CASE_COL_PX.actions,
  maxWidth: TEST_CASE_COL_PX.actions,
  boxSizing: "border-box",
  boxShadow: "-2px 0 6px rgba(16, 24, 40, 0.08)",
};

const TABLE_STICKY_CHECKBOX_BODY_SX = {
  ...TABLE_BODY_CELL_SX,
  position: "sticky",
  left: 0,
  zIndex: 4,
  width: TEST_CASE_COL_PX.checkbox,
  minWidth: TEST_CASE_COL_PX.checkbox,
  maxWidth: TEST_CASE_COL_PX.checkbox,
  px: 0,
  pl: `${TEST_CASE_CHECKBOX_COL_INSET}px`,
  pr: 0,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  ...TABLE_STICKY_CHECKBOX_BG,
};

const TABLE_STICKY_ID_BODY_SX = {
  ...TABLE_BODY_CELL_SX,
  position: "sticky",
  left: TEST_CASE_COL_PX.checkbox,
  zIndex: 4,
  width: TEST_CASE_COL_PX.id,
  minWidth: TEST_CASE_COL_PX.id,
  maxWidth: TEST_CASE_COL_PX.id,
  boxSizing: "border-box",
  boxShadow: "2px 0 6px rgba(16, 24, 40, 0.08)",
  fontSize: 13,
  fontWeight: 700,
  color: "#005BF0",
  whiteSpace: "nowrap",
  fontFamily: "Lato, sans-serif",
  ...TABLE_STICKY_CHECKBOX_BG,
};

const TABLE_STICKY_ACTIONS_BODY_SX = {
  ...TABLE_BODY_CELL_SX,
  position: "sticky",
  right: 0,
  zIndex: 4,
  width: TEST_CASE_COL_PX.actions,
  minWidth: TEST_CASE_COL_PX.actions,
  maxWidth: TEST_CASE_COL_PX.actions,
  boxSizing: "border-box",
  boxShadow: "-2px 0 6px rgba(16, 24, 40, 0.08)",
  whiteSpace: "nowrap",
  ...TABLE_STICKY_CHECKBOX_BG,
};

/** Figma 4577:22283 — search text field (32px tall: 4px py + 24px line) */
const TEST_CASES_SEARCH_FIELD_SX = {
  width: { xs: "100%", sm: 400, md: 460, lg: 520 },
  maxWidth: { xs: "100%", sm: 400, md: 460, lg: 520 },
  flex: { xs: "1 1 100%", sm: "0 0 auto" },
  minWidth: 0,
  m: 0,
  "& .MuiOutlinedInput-root": {
    height: 32,
    minHeight: 32,
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontSize: 16,
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#555555",
    pl: "10px",
    pr: "10px",
    py: "4px",
    gap: "8px",
    boxShadow: "inset 0px 2px 3px 0px rgba(85, 85, 85, 0.2)",
    "& fieldset": { border: "1px solid #707070" },
    "&:hover fieldset": { border: "1px solid #707070" },
    "&.Mui-focused fieldset": { border: "1px solid #707070" },
  },
  "& .MuiInputAdornment-root": {
    margin: 0,
    maxHeight: "unset",
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
    px: 0,
    height: 24,
    minHeight: 24,
    boxSizing: "content-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "&::placeholder": {
      color: "#707070",
      opacity: 1,
      fontFamily: "Lato, sans-serif",
      fontWeight: 400,
      fontSize: 16,
      lineHeight: "24px",
      letterSpacing: 0,
    },
  },
};

function tableColSx(colKey, hideBelow) {
  return { ...TABLE_COL_WIDTH_SX[colKey], ...responsiveCellSx(hideBelow) };
}

function responsiveCellSx(breakpoint) {
  if (!breakpoint) return {};
  return { display: { xs: "none", [breakpoint]: "table-cell" } };
}

/** Figma 4315:165200 — metric header card. */
const METRIC_LIST_HEADER_CARD_SX = {
  border: "1px solid #E6E6E6",
  borderRadius: "4px",
  bgcolor: "#FFFFFF",
  overflow: "hidden",
  width: "100%",
  boxSizing: "border-box",
};

/** Gap under the metric card when the section is expanded. */
const METRIC_LIST_TABLE_CARD_SX = {
  mt: "8px",
  width: "100%",
  boxSizing: "border-box",
};

const METRIC_SECTIONS_STACK_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "100%",
};

const METRIC_LIST_SECTION_SX = {
  width: "100%",
  minWidth: TEST_CASES_VIEW_MIN_WIDTH,
  boxSizing: "border-box",
};

/** Patch one case (or merge a shorter server list) so the review queue never shrinks. */
function mergeTestCases(prev, res, localPatch) {
  const updated = res?.data?.testCase;
  const incoming = res?.data?.testCases;
  let next = prev;
  if (Array.isArray(incoming) && incoming.length) {
    const byId = new Map(incoming.map((tc) => [tc.id, tc]));
    next = prev.map((tc) => byId.get(tc.id) || tc);
  }
  if (updated?.id) {
    next = next.map((tc) => (tc.id === updated.id ? { ...tc, ...updated } : tc));
  }
  if (localPatch?.id) {
    next = next.map((tc) => {
      if (tc.id !== localPatch.id) return tc;
      return {
        ...tc,
        ...(localPatch.status ? { status: localPatch.status } : {}),
        ...(localPatch.name ? { name: localPatch.name } : {}),
        ...(localPatch.trustAttribute ? { trustAttribute: localPatch.trustAttribute } : {}),
        ...(localPatch.testType ? { testType: localPatch.testType } : {}),
        ...(localPatch.metricId ? { metricId: localPatch.metricId } : {}),
        ...(localPatch.input ? { input: localPatch.input } : {}),
      };
    });
  }
  return next;
}

export default function TestCasesPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const autoGeneratePending = useRef(location.state?.autoGenerate === true);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const [appName, setAppName] = useState("");
  const [testCases, setTestCases] = useState([]);
  const [risks, setRisks] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [canGenerate, setCanGenerate] = useState(false);
  const [strategyRequired, setStrategyRequired] = useState(false);
  const [isContentGenerator, setIsContentGenerator] = useState(false);

  const [statusTab, setStatusTab] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedMetricIds, setExpandedMetricIds] = useState(() => new Set());
  const [metricVisibleLimits, setMetricVisibleLimits] = useState({});
  const [reviewQueueMetricId, setReviewQueueMetricId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(isDesktop);
  const [trustSectionOpen, setTrustSectionOpen] = useState(false);
  const [riskSectionOpen, setRiskSectionOpen] = useState(false);
  const [metricSectionOpen, setMetricSectionOpen] = useState(false);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [selectedRiskIds, setSelectedRiskIds] = useState([]);
  const [selectedMetricIds, setSelectedMetricIds] = useState([]);
  const selectionStoreRef = useRef(null);
  if (!selectionStoreRef.current) selectionStoreRef.current = createSelectionStore();
  const selectionStore = selectionStoreRef.current;
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);

  const TEST_CASE_FLYOUT_CLOSED = { open: false, testCase: null, viewOnly: false };
  const [testCaseFlyout, setTestCaseFlyout] = useState(TEST_CASE_FLYOUT_CLOSED);
  const closeTestCaseFlyout = useCallback(() => setTestCaseFlyout(TEST_CASE_FLYOUT_CLOSED), []);
  const [testCaseToDelete, setTestCaseToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [testCaseToSuspend, setTestCaseToSuspend] = useState(null);
  const [suspending, setSuspending] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [mixedSelectionOpen, setMixedSelectionOpen] = useState(false);
  const [reviewQueueOpen, setReviewQueueOpen] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [confirmingMetricId, setConfirmingMetricId] = useState(null);
  const [groundTruthDialog, setGroundTruthDialog] = useState(null);
  const [rowGroundTruthDialog, setRowGroundTruthDialog] = useState(null);
  const [confirmingRowId, setConfirmingRowId] = useState(null);

  // Keep filter panel in sync with viewport — sidebar on md+, collapsed rail on smaller screens
  useEffect(() => {
    setFiltersOpen(isDesktop);
  }, [isDesktop]);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchTestCases(projectId);
      if (!res?.success) {
        setError(res?.error || "Failed to load test cases.");
        return;
      }
      log("✓ test cases fetched — count:", res.data?.testCases?.length || 0);
      setAppName(res.data?.appName || "");
      setTestCases(res.data?.testCases || []);
      setRisks(res.data?.risks || []);
      setMetrics(res.data?.metrics || []);
      setCanGenerate(Boolean(res.data?.canGenerate));
      setStrategyRequired(Boolean(res.data?.strategyRequired));
      setIsContentGenerator(Boolean(res.data?.isContentGenerator));
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to load test cases.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (!location.state?.autoGenerate) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state?.autoGenerate, navigate]);

  const handleGenerate = useCallback(async () => {
    setError("");
    try {
      setGenerating(true);
      const res = await generateTestCases(projectId);
      if (!res?.success) {
        setError(res?.error || "Test case generation failed.");
        return;
      }
      setTestCases(res.data?.testCases || []);
      if (Array.isArray(res.data?.metrics)) setMetrics(res.data.metrics);
      if (Array.isArray(res.data?.risks)) setRisks(res.data.risks);
      setCanGenerate(Boolean(res.data?.canGenerate ?? true));
      if (res.data?.isContentGenerator != null) {
        setIsContentGenerator(Boolean(res.data.isContentGenerator));
      }
      const generatedCount = res.data?.generatedCount ?? 0;
      const mode = res.data?.mode;
      if (mode === "unchanged") {
        setToast("No new risks or metrics added. Existing test cases unchanged.");
      } else if (mode === "incremental") {
        setToast(`Generated ${generatedCount} test cases for new risk(s)/metric(s).`);
      } else {
        setToast(`Generated ${generatedCount} test cases.`);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Test case generation failed.");
    } finally {
      setGenerating(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (loading || !autoGeneratePending.current) return;
    autoGeneratePending.current = false;
    if (canGenerate) {
      handleGenerate();
    }
  }, [loading, canGenerate, handleGenerate]);

  const handleAdd = async (payload) => {
    try {
      const res = await addTestCase(projectId, payload);
      if (!res?.success) {
        setError(res?.error || "Failed to add test case.");
        return;
      }
      setTestCases(res.data?.testCases || []);
      closeTestCaseFlyout();
      setToast("Test cases added successfully.");
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to add test case.");
    }
  };

  const handleEditSave = async (payload) => {
    if (!testCaseFlyout.testCase?.id) return handleAdd(payload);
    try {
      const res = await updateTestCase(projectId, testCaseFlyout.testCase.id, payload);
      if (!res?.success) {
        setError(res?.error || "Failed to update test case.");
        return;
      }
      setTestCases(res.data?.testCases || []);
      closeTestCaseFlyout();
      setToast("Test case updated.");
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to update test case.");
    }
  };

  const handleViewTestCase = useCallback((row) => {
    setTestCaseFlyout({ open: true, testCase: row, viewOnly: true });
  }, []);

  const handleEditTestCase = useCallback((row) => {
    setTestCaseFlyout({ open: true, testCase: row, viewOnly: false });
  }, []);

  const handleReviewStatus = async (tc, status) => {
    if (!tc?.id) return false;
    try {
      setReviewSaving(true);
      const res = await updateTestCase(projectId, tc.id, { status });
      if (!res?.success) {
        setError(res?.error || "Failed to update test case.");
        return false;
      }
      setTestCases((prev) => mergeTestCases(prev, res, { id: tc.id, status }));
      if (status === "verified") {
        setToast(`${tc.name || "Test case"} ground truth reviewed successfully`);
      } else {
        setToast("Test case flagged for later review.");
      }
      return true;
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to update test case.");
      return false;
    } finally {
      setReviewSaving(false);
    }
  };

  const handleReviewEditSave = async (tc, payload) => {
    if (!tc?.id) return false;
    try {
      setReviewSaving(true);
      const res = await updateTestCase(projectId, tc.id, payload);
      if (!res?.success) {
        setError(res?.error || "Failed to update test case.");
        return false;
      }
      setTestCases((prev) =>
        mergeTestCases(prev, res, {
          id: tc.id,
          status: payload.status,
          name: payload.name,
          trustAttribute: payload.trust_attribute,
          testType: payload.test_type,
          metricId: payload.metric_id,
          input: payload.input,
        })
      );
      if (payload?.status === "verified") {
        setToast(`${tc.name || "Test case"} ground truth reviewed successfully`);
      } else {
        setToast("Test case updated.");
      }
      return true;
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to update test case.");
      return false;
    } finally {
      setReviewSaving(false);
    }
  };

  const openMixedSelectionDialog = () => setMixedSelectionOpen(true);

  const selectionIsMixed = (rows) => isMixedSourceSelection(selectedRowsFrom(selectionStore, rows));

  const handleSuspendClick = (tc) => {
    if (!isGeneratedLineageSource(tc?.source) || tc?.status === "deactivated") return;
    setBulkAction(null);
    setTestCaseToSuspend(tc);
  };

  const handleSuspendConfirm = async () => {
    if (bulkAction === "deactivate" && selectionIsMixed(filtered)) {
      openMixedSelectionDialog();
      return;
    }
    const targets =
      bulkAction === "deactivate"
        ? filtered.filter(
            (t) =>
              selectionStore.has(t.id) &&
              isGeneratedLineageSource(t.source) &&
              t.status !== "deactivated"
          )
        : testCaseToSuspend &&
            isGeneratedLineageSource(testCaseToSuspend.source) &&
            testCaseToSuspend.status !== "deactivated"
          ? [testCaseToSuspend]
          : [];
    if (!targets.length) return;
    try {
      setSuspending(true);
      let latest = null;
      for (const tc of targets) {
        const res = await updateTestCase(projectId, tc.id, { status: "deactivated" });
        if (!res?.success) {
          setError(res?.error || "Failed to deactivate test case.");
          return;
        }
        latest = res;
      }
      setTestCases(latest?.data?.testCases || []);
      setToast(targets.length > 1 ? `${targets.length} test cases deactivated.` : "Test case deactivated.");
      setTestCaseToSuspend(null);
      setBulkAction(null);
      if (bulkAction === "deactivate") selectionStore.clear();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to deactivate test case.");
    } finally {
      setSuspending(false);
    }
  };

  const handleReactivateClick = (tc) => {
    if (!isGeneratedLineageSource(tc?.source) || tc?.status !== "deactivated") return;
    setBulkAction(null);
    setTestCaseToSuspend(tc);
  };

  const handleReactivateConfirm = async () => {
    if (bulkAction === "reactivate" && selectionIsMixed(filtered)) {
      openMixedSelectionDialog();
      return;
    }
    const targets =
      bulkAction === "reactivate"
        ? filtered.filter(
            (t) =>
              selectionStore.has(t.id) &&
              isGeneratedLineageSource(t.source) &&
              t.status === "deactivated"
          )
        : testCaseToSuspend &&
            isGeneratedLineageSource(testCaseToSuspend.source) &&
            testCaseToSuspend.status === "deactivated"
          ? [testCaseToSuspend]
          : [];
    if (!targets.length) return;
    try {
      setSuspending(true);
      let latest = null;
      for (const tc of targets) {
        const res = await updateTestCase(projectId, tc.id, { status: "not_reviewed" });
        if (!res?.success) {
          setError(res?.error || "Failed to reactivate test case.");
          return;
        }
        latest = res;
      }
      setTestCases(latest?.data?.testCases || []);
      setToast(targets.length > 1 ? `${targets.length} test cases reactivated.` : "Test case reactivated.");
      setTestCaseToSuspend(null);
      setBulkAction(null);
      if (bulkAction === "reactivate") selectionStore.clear();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to reactivate test case.");
    } finally {
      setSuspending(false);
    }
  };

  const handleDeleteClick = (tc) => {
    if (statusTab === "deactivated" || tc?.source !== "Custom") return;
    setBulkAction(null);
    setTestCaseToDelete(tc);
  };

  const handleConfirmGroundTruthForRow = useCallback((tc) => {
    if (tc?.status !== "not_reviewed") return;
    setRowGroundTruthDialog(tc);
  }, []);

  const executeConfirmGroundTruthForRow = useCallback(
    async (groundTruth) => {
      const tc = rowGroundTruthDialog;
      if (!tc?.id || tc.status !== "not_reviewed") return;

      try {
        setConfirmingRowId(tc.id);
        setError("");
        const res = await updateTestCase(projectId, tc.id, {
          status: "verified",
          groundTruth: groundTruth || tc.groundTruth,
        });
        if (!res?.success) {
          setError(res?.error || "Failed to confirm ground truth.");
          return;
        }
        if (res?.data?.testCases) {
          setTestCases(res.data.testCases);
        } else {
          setTestCases((prev) =>
            mergeTestCases(prev, res, {
              id: tc.id,
              status: "verified",
              groundTruth: groundTruth || tc.groundTruth,
            })
          );
        }
        setRowGroundTruthDialog(null);
      } catch (e) {
        setError(e.response?.data?.error || e.message || "Failed to confirm ground truth.");
      } finally {
        setConfirmingRowId(null);
      }
    },
    [rowGroundTruthDialog, projectId]
  );

  const handleDeleteConfirm = async () => {
    if (statusTab === "deactivated") return;
    if (bulkAction === "delete" && selectionIsMixed(filtered)) {
      openMixedSelectionDialog();
      return;
    }
    const targets =
      bulkAction === "delete"
        ? filtered.filter((t) => selectionStore.has(t.id) && t.source === "Custom")
        : testCaseToDelete && testCaseToDelete.source === "Custom"
          ? [testCaseToDelete]
          : [];
    if (!targets.length) return;
    try {
      setDeleting(true);
      let latest = null;
      for (const tc of targets) {
        const res = await deleteTestCase(projectId, tc.id);
        if (!res?.success) {
          setError(res?.error || "Failed to delete test case.");
          return;
        }
        latest = res;
      }
      setTestCases(latest?.data?.testCases || []);
      setToast(targets.length > 1 ? `${targets.length} test cases deleted.` : "Test case deleted.");
      setTestCaseToDelete(null);
      setBulkAction(null);
      if (bulkAction === "delete") selectionStore.clear();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to delete test case.");
    } finally {
      setDeleting(false);
    }
  };

  const metricCodeById = useMemo(
    () => new Map(metrics.map((m, i) => [m.id, `Metric${String(i + 1).padStart(3, "0")}`])),
    [metrics]
  );

  const metricOrderById = useMemo(() => {
    const map = new Map();
    metrics.forEach((m, i) => map.set(String(m.id), i));
    return map;
  }, [metrics]);

  const riskIdByMetricId = useMemo(
    () => new Map(metrics.map((m) => [String(m.id), m.riskId])),
    [metrics]
  );

  const resolveTestCaseRiskId = useCallback(
    (tc) => tc.riskId ?? riskIdByMetricId.get(String(tc.metricId)) ?? null,
    [riskIdByMetricId]
  );

  const numbered = useMemo(() => {
    const activeMetricIds = new Set(metrics.map((m) => String(m.id)));
    const activeRiskIds = new Set(risks.map((r) => String(r.id)));
    const visible = testCases.filter(
      (tc) =>
        (!tc.metricId || activeMetricIds.has(String(tc.metricId))) &&
        (!resolveTestCaseRiskId(tc) || activeRiskIds.has(String(resolveTestCaseRiskId(tc))))
    );
    const byMetric = [...visible].sort((a, b) => {
      const ai = metricOrderById.has(String(a.metricId))
        ? metricOrderById.get(String(a.metricId))
        : Number.MAX_SAFE_INTEGER;
      const bi = metricOrderById.has(String(b.metricId))
        ? metricOrderById.get(String(b.metricId))
        : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    });
    return byMetric.map((tc) => ({
      ...tc,
      metricCode: metricCodeById.get(tc.metricId) || "",
    }));
  }, [testCases, metrics, risks, metricCodeById, metricOrderById, resolveTestCaseRiskId]);

  const stats = useMemo(() => {
    const uniqueIds = new Set(
      numbered.map((t) => String(t.id)).filter(Boolean)
    );

    const total = uniqueIds.size || numbered.length;
    const verified = countUniqueByStatus(numbered, "verified");
    const pending = countUniqueByStatus(numbered, "not_reviewed");
    const flagged = countUniqueByStatus(numbered, "flagged");

    const humanAuthored = new Set(
      numbered
        .filter((t) => t.source === "Custom" && t.id != null)
        .map((t) => String(t.id))
    ).size;

    const generated = new Set(
      numbered
        .filter((t) => isGeneratedLineageSource(t.source) && t.id != null)
        .map((t) => String(t.id))
    ).size;

    const deactivated = countUniqueByStatus(numbered, "deactivated");

    return {
      total,
      verified,
      pending,
      flagged,
      humanAuthored,
      generated,
      deactivated,
    };
  }, [numbered]);

  const attributeCounts = useMemo(() => {
    const counts = Object.fromEntries(TRUST_ATTRIBUTES.map((a) => [a, 0]));
    numbered.forEach((tc) => {
      if (tc.trustAttribute && counts[tc.trustAttribute] !== undefined) {
        counts[tc.trustAttribute] += 1;
      }
    });
    return counts;
  }, [numbered]);

  const riskCounts = useMemo(() => {
    const counts = new Map();
    risks.forEach((r) => counts.set(String(r.id), 0));
    numbered.forEach((tc) => {
      const riskId = resolveTestCaseRiskId(tc);
      const key = riskId != null ? String(riskId) : "";
      if (key && counts.has(key)) counts.set(key, counts.get(key) + 1);
    });
    return counts;
  }, [numbered, risks, resolveTestCaseRiskId]);

  const metricCounts = useMemo(() => {
    const counts = new Map();
    metrics.forEach((m) => counts.set(String(m.id), 0));
    numbered.forEach((tc) => {
      const key = tc.metricId != null ? String(tc.metricId) : "";
      if (key && counts.has(key)) counts.set(key, counts.get(key) + 1);
    });
    return counts;
  }, [numbered, metrics]);

  const filtered = useMemo(() => {
    let list = numbered;

    if (statusTab === "pending") list = list.filter((t) => t.status === "not_reviewed");
    else if (statusTab === "flagged") list = list.filter((t) => t.status === "flagged");
    else if (statusTab === "verified") list = list.filter((t) => t.status === "verified");
    else if (statusTab === "generated") list = list.filter((t) => isGeneratedLineageSource(t.source));
    else if (statusTab === "custom") list = list.filter((t) => t.source === "Custom");
    else if (statusTab === "deactivated") list = list.filter((t) => t.status === "deactivated");

    // Sidebar: union of every checked item (trust OR risk OR metric). Empty sidebar = no extra filter.
    const hasSidebarFilters =
      selectedAttributes.length > 0 || selectedRiskIds.length > 0 || selectedMetricIds.length > 0;
    if (hasSidebarFilters) {
      list = list.filter((t) => {
        if (selectedAttributes.length && selectedAttributes.includes(t.trustAttribute)) return true;
        if (selectedRiskIds.length && filterIdInList(selectedRiskIds, resolveTestCaseRiskId(t))) {
          return true;
        }
        if (selectedMetricIds.length && filterIdInList(selectedMetricIds, t.metricId)) return true;
        return false;
      });
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (t) =>
          t.name?.toLowerCase().includes(q) ||
          t.riskName?.toLowerCase().includes(q) ||
          t.metricName?.toLowerCase().includes(q) ||
          t.metricCode?.toLowerCase().includes(q) ||
          promptPreview(t).toLowerCase().includes(q)
      );
    }
    return list;
  }, [
    numbered,
    statusTab,
    selectedAttributes,
    selectedRiskIds,
    selectedMetricIds,
    search,
    resolveTestCaseRiskId,
  ]);

  const sidebarFiltersActiveCount =
    selectedAttributes.length + selectedRiskIds.length + selectedMetricIds.length;

  useEffect(() => {
    setMetricVisibleLimits({});
  }, [statusTab, selectedAttributes, selectedRiskIds, selectedMetricIds, search]);

  const metricGroups = useMemo(() => {
    const casesByMetric = new Map();
    filtered.forEach((tc) => {
      const key = tc.metricId != null ? String(tc.metricId) : "__none__";
      if (!casesByMetric.has(key)) casesByMetric.set(key, []);
      casesByMetric.get(key).push(tc);
    });

    const orderedIds = metrics
      .filter((m) => m.enabled !== false)
      .sort(
        (a, b) =>
          (metricOrderById.get(String(a.id)) ?? 0) - (metricOrderById.get(String(b.id)) ?? 0)
      )
      .map((m) => String(m.id))
      .filter((id) => casesByMetric.has(id));

    casesByMetric.forEach((_, metricId) => {
      if (metricId !== "__none__" && !orderedIds.includes(metricId)) {
        orderedIds.push(metricId);
      }
    });
    if (casesByMetric.has("__none__")) orderedIds.push("__none__");

    return orderedIds.map((metricId) => {
      const cases = casesByMetric.get(metricId) || [];
      const metric = metrics.find((m) => filterIdEquals(m.id, metricId));
      const first = cases[0];
      const activeCases = cases.filter((tc) => tc.status !== "deactivated");
      const verifiedCount = activeCases.filter((tc) => tc.status === "verified").length;
      const pendingGroundTruthCount = activeCases.filter((tc) => tc.status !== "verified").length;
      return {
        metricId,
        metricName: metric?.metricName || first?.metricName || "Unlinked metric",
        testCases: cases,
        total: cases.length,
        activeCount: activeCases.length,
        verifiedCount,
        canConfirmGroundTruth: pendingGroundTruthCount > 0,
      };
    });
  }, [filtered, metrics, metricOrderById]);

  useEffect(() => {
    setExpandedMetricIds((prev) => {
      const ids = metricGroups.map((g) => g.metricId);
      if (!ids.length) return new Set();
      const next = new Set([...prev].filter((id) => ids.includes(id)));
      if (!next.size) next.add(ids[0]);
      return next;
    });
  }, [metricGroups]);

  const getMetricVisibleLimit = useCallback(
    (metricId, total) => Math.min(metricVisibleLimits[metricId] ?? METRIC_INITIAL_VISIBLE, total),
    [metricVisibleLimits]
  );

  const loadMoreMetricCases = useCallback((metricId, total) => {
    setMetricVisibleLimits((prev) => ({
      ...prev,
      [metricId]: Math.min((prev[metricId] ?? METRIC_INITIAL_VISIBLE) + METRIC_LOAD_MORE_STEP, total),
    }));
  }, []);

  const showLessMetricCases = useCallback((metricId) => {
    setMetricVisibleLimits((prev) => ({
      ...prev,
      [metricId]: METRIC_INITIAL_VISIBLE,
    }));
  }, []);

  const toggleMetricExpanded = useCallback((metricId) => {
    setExpandedMetricIds((prev) => {
      const next = new Set(prev);
      if (next.has(metricId)) next.delete(metricId);
      else next.add(metricId);
      return next;
    });
  }, []);

  const visibleRows = useMemo(() => {
    const rows = [];
    metricGroups.forEach((group) => {
      if (!expandedMetricIds.has(group.metricId)) return;
      const limit = getMetricVisibleLimit(group.metricId, group.total);
      rows.push(...group.testCases.slice(0, limit));
    });
    return rows;
  }, [metricGroups, expandedMetricIds, getMetricVisibleLimit]);

  const reviewQueueTestCases = useMemo(
    () =>
      reviewQueueMetricId
        ? numbered.filter((tc) => tc.metricId === reviewQueueMetricId)
        : numbered,
    [numbered, reviewQueueMetricId]
  );

  const hasAny = numbered.length > 0;

  const toggleSelectAll = () => {
    const ids = visibleRows.map((tc) => tc.id);
    const allOn = ids.length > 0 && ids.every((id) => selectionStore.has(id));
    selectionStore.toggleAll(ids, !allOn);
  };

  const openReviewQueueForMetric = useCallback((metricId) => {
    setReviewQueueMetricId(metricId);
    setReviewQueueOpen(true);
  }, []);

  const requestConfirmGroundTruthForMetric = useCallback(
    (metricId) => {
      const group = metricGroups.find((g) => filterIdEquals(g.metricId, metricId));
      const targets = pendingGroundTruthInGroup(group);
      if (!targets.length) {
        const visibleActive = (group?.testCases || []).filter((tc) => tc.status !== "deactivated");
        if (!visibleActive.length) {
          setToast("No active test cases in this metric.");
        } else {
          setToast("All test cases in this metric are already confirmed.");
        }
        return;
      }

      setGroundTruthDialog({
        metricId,
        metricName: group?.metricName || "this metric",
        confirmCount: targets.length,
        targetIds: targets.map((tc) => tc.id),
        sourceSummary: sourceBreakdownForCases(targets),
        otherMetricsLabel: formatOtherMetricGroups(metricGroups, metricId),
      });
    },
    [metricGroups]
  );

  const executeConfirmGroundTruthForMetric = useCallback(async () => {
    const metricId = groundTruthDialog?.metricId;
    if (!metricId) return;

    const targetIds = new Set((groundTruthDialog.targetIds || []).map(String));
    const group = metricGroups.find((g) => filterIdEquals(g.metricId, metricId));
    const targets = (
      targetIds.size
        ? numbered.filter((tc) => targetIds.has(String(tc.id)))
        : pendingGroundTruthInGroup(group)
    ).filter((tc) => tc.status !== "deactivated" && tc.status !== "verified");
    if (!targets.length) {
      setGroundTruthDialog(null);
      return;
    }

    try {
      setConfirmingMetricId(metricId);
      setError("");
      let latest = null;
      for (const tc of targets) {
        const res = await updateTestCase(projectId, tc.id, { status: "verified" });
        if (!res?.success) {
          setError(res?.error || "Failed to confirm ground truth.");
          return;
        }
        latest = res;
      }
      if (latest?.data?.testCases) {
        setTestCases(latest.data.testCases);
      } else {
        setTestCases((prev) => mergeTestCases(prev, latest));
      }
      setGroundTruthDialog(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to confirm ground truth.");
    } finally {
      setConfirmingMetricId(null);
    }
  }, [groundTruthDialog, metricGroups, numbered, projectId]);

  const toggleSelectOne = (id) => {
    selectionStore.toggle(id);
  };

  const deleteDialogItems =
    bulkAction === "delete"
      ? filtered.filter((t) => selectionStore.has(t.id) && t.source === "Custom")
      : testCaseToDelete
        ? [testCaseToDelete]
        : [];
  const suspendDialogItems =
    bulkAction === "deactivate"
      ? filtered.filter(
          (t) =>
            selectionStore.has(t.id) &&
            isGeneratedLineageSource(t.source) &&
            t.status !== "deactivated"
        )
      : bulkAction === "reactivate"
        ? filtered.filter(
            (t) =>
              selectionStore.has(t.id) &&
              isGeneratedLineageSource(t.source) &&
              t.status === "deactivated"
          )
        : testCaseToSuspend
          ? [testCaseToSuspend]
          : [];

  const handleBulkDeactivate = () => {
    if (statusTab === "deactivated") return;
    if (selectionIsMixed(filtered)) {
      openMixedSelectionDialog();
      return;
    }
    const targets = filtered.filter(
      (t) =>
        selectionStore.has(t.id) &&
        isGeneratedLineageSource(t.source) &&
        t.status !== "deactivated"
    );
    if (!targets.length) return;
    setBulkAction("deactivate");
    setTestCaseToSuspend(targets.length === 1 ? targets[0] : { ...targets[0], name: `${targets.length} selected test cases` });
  };

  const handleBulkReactivate = () => {
    if (selectionIsMixed(filtered)) {
      openMixedSelectionDialog();
      return;
    }
    const targets = filtered.filter(
      (t) =>
        selectionStore.has(t.id) &&
        isGeneratedLineageSource(t.source) &&
        t.status === "deactivated"
    );
    if (!targets.length) return;
    setBulkAction("reactivate");
    setTestCaseToSuspend(targets.length === 1 ? targets[0] : { ...targets[0], name: `${targets.length} selected test cases` });
  };

  const handleBulkDelete = () => {
    if (statusTab === "deactivated") return;
    if (selectionIsMixed(filtered)) {
      openMixedSelectionDialog();
      return;
    }
    const targets = filtered.filter((t) => selectionStore.has(t.id) && t.source === "Custom");
    if (!targets.length) return;
    setBulkAction("delete");
    setTestCaseToDelete(targets.length === 1 ? targets[0] : { ...targets[0], name: `${targets.length} selected test cases` });
  };

  const resetSidebarFilters = () => {
    setSelectedAttributes([]);
    setSelectedRiskIds([]);
    setSelectedMetricIds([]);
  };

  const toggleAttributeFilter = (attr) => {
    setSelectedAttributes((prev) =>
      prev.includes(attr) ? prev.filter((a) => a !== attr) : [...prev, attr]
    );
  };

  const allTrustAttributesSelected =
    TRUST_ATTRIBUTES.length > 0 &&
    TRUST_ATTRIBUTES.every((attr) => selectedAttributes.includes(attr));

  const selectAllTrustAttributes = (e) => {
    e.stopPropagation();
    setSelectedAttributes(allTrustAttributesSelected ? [] : [...TRUST_ATTRIBUTES]);
  };

  const sidebarRisks = useMemo(
    () => risks.filter((r) => r.enabled !== false),
    [risks]
  );

  const allSidebarRisksSelected =
    sidebarRisks.length > 0 &&
    sidebarRisks.every((r) => filterIdInList(selectedRiskIds, r.id));

  const selectAllRisks = (e) => {
    e.stopPropagation();
    setSelectedRiskIds(allSidebarRisksSelected ? [] : sidebarRisks.map((r) => r.id));
  };

  const sidebarMetrics = useMemo(
    () => metrics.filter((m) => m.enabled !== false),
    [metrics]
  );

  const allSidebarMetricsSelected =
    sidebarMetrics.length > 0 &&
    sidebarMetrics.every((m) => filterIdInList(selectedMetricIds, m.id));

  const selectAllMetrics = (e) => {
    e.stopPropagation();
    setSelectedMetricIds(allSidebarMetricsSelected ? [] : sidebarMetrics.map((m) => m.id));
  };

  const openFiltersPanel = useCallback((focus = "filters") => {
    setFiltersOpen(true);
    if (focus === "trust") {
      setTrustSectionOpen(true);
      setRiskSectionOpen(false);
      setMetricSectionOpen(false);
    } else if (focus === "risks") {
      setTrustSectionOpen(false);
      setRiskSectionOpen(true);
      setMetricSectionOpen(false);
    } else if (focus === "metrics") {
      setTrustSectionOpen(false);
      setRiskSectionOpen(false);
      setMetricSectionOpen(true);
    } else {
      setTrustSectionOpen(true);
      setRiskSectionOpen(false);
      setMetricSectionOpen(false);
    }
  }, []);

  const toggleRiskFilter = (riskId) => {
    setSelectedRiskIds((prev) =>
      filterIdInList(prev, riskId) ? prev.filter((id) => !filterIdEquals(id, riskId)) : [...prev, riskId]
    );
  };

  const toggleMetricFilter = (metricId) => {
    setSelectedMetricIds((prev) =>
      filterIdInList(prev, metricId) ? prev.filter((id) => !filterIdEquals(id, metricId)) : [...prev, metricId]
    );
  };

  const STATUS_TABS = [
    { id: "all", label: `All Test Cases (${stats.total})` },
    { id: "pending", label: `Pending Review (${stats.pending})` },
    { id: "flagged", label: `Flagged (${stats.flagged})` },
    { id: "verified", label: `Validated (${stats.verified})` },
    { id: "generated", label: `Generated (${stats.generated})` },
    { id: "custom", label: `Custom (${stats.humanAuthored})` },
    { id: "deactivated", label: `Deactivated (${stats.deactivated})` },
  ];

  return (
    <SelectDropdownTheme>
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#F5F5F5", minWidth: 0 }}>
      <IntakeGateTabsBar
        activeGate={ACTIVE_GATE}
        unlockedUpTo={ACTIVE_GATE}
        onGateClick={(gateIndex) => {
          if (gateIndex === 0) navigate(`/chat/${projectId}`);
          if (gateIndex === 1) navigate(`/gate1/${projectId}`);
        }}
        showBack={false}
        showViewSummary={false}
      />

      {/* Sub-tabs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          pl: GATE_SUBTABS_PL,
          pr: GATE_SUBTABS_PR,
          height: 50,
          minHeight: 50,
          bgcolor: "#fff",
          borderBottom: "1px solid #E6E6E6",
          flexShrink: 0,
          overflowX: "auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "stretch", gap: GATE_SUBTABS_GAP, minWidth: "min-content" }}>
          {DOC_TABS.map((t, i) => {
            const active = i === 1;
            const tabRoutes = {
              "Test Strategies": `/test-strategy/${projectId}`,
              "Test Cases": `/test-cases/${projectId}`,
              "Test Suite": `/test-suites/${projectId}`,
            };
            const clickable = i !== 1;
            const Icon = t.Icon;
            return (
              <Box
                key={t.label}
                onClick={() => clickable && navigate(tabRoutes[t.label])}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  height: "100%",
                  py: "8px",
                  boxSizing: "border-box",
                  cursor: clickable ? "pointer" : "default",
                  color: active ? TAB_ACTIVE : TAB_INACTIVE,
                  "& svg": { width: 17, height: 17, display: "block", flexShrink: 0 },
                  "& svg path": { stroke: "currentColor" },
                  ...(active && {
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: "-8px",
                      right: "-8px",
                      bottom: 0,
                      height: "3px",
                      bgcolor: TAB_UNDERLINE,
                      pointerEvents: "none",
                    },
                  }),
                }}
              >
                <Icon aria-hidden />
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontSize: 14,
                    fontWeight: active ? 700 : 400,
                    lineHeight: "18px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ ml: "auto", pl: 2, display: "flex", alignItems: "center" }}>
          <AiProvenanceLink projectId={projectId} steps={["Test cases"]} refreshKey={generating} />
        </Box>
      </Box>

      {loading || generating ? (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
          <CircularProgress />
          <Typography sx={{ fontSize: 14, color: "#667085", fontWeight: 600 }}>
            {generating ? "Generating test cases…" : "Loading test cases…"}
          </Typography>
        </Box>
      ) : !hasAny ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
          <Box sx={{ textAlign: "center", maxWidth: 460 }}>
            <FactCheckOutlinedIcon sx={{ fontSize: 48, color: "#98A2B3", mb: 1 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#344054", mb: 0.5 }}>
              No test cases yet
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#667085", mb: 2 }}>
              {strategyRequired
                ? "Generate the test strategy first, then generate concrete test cases per metric."
                : canGenerate
                ? "Generate concrete, executable test cases from your risks, metrics, and test strategy."
                : "Complete Gate 1 (trust weights, risks, metrics) and the test strategy first."}
            </Typography>
            <Button
              variant="contained"
              disableElevation
              disableRipple
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              disabled={generating || !canGenerate}
              onClick={handleGenerate}
              sx={PAGE_PRIMARY_BTN_MD_SX}
            >
              Generate Test Cases
            </Button>
          </Box>
        </Box>
      ) : reviewQueueOpen ? (
        <ReviewQueueView
          testCases={reviewQueueTestCases}
          metrics={metrics}
          risks={risks}
          isContentGenerator={isContentGenerator}
          onClose={() => {
            setReviewQueueOpen(false);
            setReviewQueueMetricId(null);
          }}
          onUpdateStatus={handleReviewStatus}
          onSaveEdit={handleReviewEditSave}
          onSuspend={handleSuspendClick}
          onSubmitForApproval={() => {
            setToast("Set sent for sign off.");
          }}
          onGoToTestSuite={() => {
            setReviewQueueOpen(false);
            navigate(`/test-suites/${projectId}`);
          }}
          busy={reviewSaving}
        />
      ) : (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
          {/* Page header */}
          <Box
            sx={{
              pl: `${HEADER_INSET_LEFT_PX}px`,
              pr: { xs: 2, md: 3 },
              py: { xs: 1.5, md: 2 },
              boxSizing: "border-box",
              bgcolor: "#fff",
              borderBottom: "1px solid #E6E6E6",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "flex-start" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: `${HEADER_ROW_GAP_PX}px`,
                  flex: "1 1 auto",
                  minWidth: 0,
                }}
              >
                <IntakeHeaderBackButton
                  onClick={() => navigate(`/test-strategy/${projectId}`)}
                  aria-label="Back to test strategy"
                />
                <Box sx={{ ...STICKY_PAGE_HEADER_TEXT_COLUMN_SX, ml: `${TITLE_NUDGE_LEFT_PX}px` }}>
                  <Typography
                    sx={{
                      fontSize: { xs: 18, sm: 20, md: 22 },
                      fontWeight: 800,
                      color: "#111827",
                      fontFamily: "Lato, sans-serif",
                      m: 0,
                    }}
                  >
                    {appName || "AI System"}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 12, md: 13 }, color: "#667085", m: 0 }}>
                    View test cases generated by system or create your own.
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: 3,
                  flexShrink: 0,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {SHOW_REVIEW_QUEUE_BUTTON ? (
                  <Button
                    variant="outlined"
                    disableElevation
                    disableRipple
                    onClick={() => {
                      setReviewQueueMetricId(null);
                      setReviewQueueOpen(true);
                    }}
                    sx={{
                      ...GHOST_PRIMARY_BTN_SX,
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    Review Queue
                  </Button>
                ) : null}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "stretch",
                    height: 32,
                    width: { xs: "100%", sm: "auto" },
                    borderRadius: "4px",
                    boxShadow: "0px 1px 2px 0px #0000000A",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <Button
                    variant="contained"
                    disableElevation
                    disableRipple
                    onClick={() => {
                      setTestCaseFlyout({ open: true, testCase: null, viewOnly: false });
                    }}
                    sx={PAGE_PRIMARY_SPLIT_MAIN_SX}
                  >
                    Add Test Case
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    disableRipple
                    aria-label="Add Test Case options"
                    onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                    sx={PAGE_PRIMARY_SPLIT_CARET_SX}
                  >
                    <KeyboardArrowDownIcon style={{ width: 16, height: 16, display: "block" }} />
                  </Button>
                </Box>
                <Menu
                  anchorEl={addMenuAnchor}
                  open={Boolean(addMenuAnchor)}
                  onClose={() => setAddMenuAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setAddMenuAnchor(null);
                      navigate(`/test-cases/${projectId}/upload`);
                    }}
                  >
                    Upload test case
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAddMenuAnchor(null);
                      setToast("Import test case — coming soon.");
                    }}
                  >
                    Import test case
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Box>

          {/* Main body: filters + content */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {filtersOpen ? (
              <Box
                sx={{
                  width: { xs: "100%", md: 280, lg: 300, xl: 320 },
                  maxWidth: { xs: "100%", md: 320 },
                  flexShrink: 0,
                  bgcolor: "#fff",
                  borderRight: { xs: "none", md: "1px solid #E6E6E6" },
                  borderBottom: { xs: "1px solid #E6E6E6", md: "none" },
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                  maxHeight: { xs: 280, sm: 320, md: "none" },
                  boxSizing: "border-box",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: SIDEBAR_PANEL_PX,
                    py: 1.5,
                    borderBottom: "1px solid #E6E6E6",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                    <FilterIcon style={{ width: 16, height: 16, display: "block", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#344054" }}>Filters</Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      flexShrink: 0,
                      ml: "auto",
                    }}
                  >
                    <Button
                size="small"
                      disableRipple
                      onClick={resetSidebarFilters}
                      sx={{
                        ...PRIMARY_BORDERLESS_SX,
                        minWidth: 0,
                        width: "fit-content",
                        flexShrink: 0,
                        px: 0,
                      }}
                    >
                      Reset
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => setFiltersOpen(false)}
                      aria-label="Close filters"
                      sx={SIDEBAR_FILTERS_CLOSE_BTN_SX}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>

                {/* Trust attributes */}
                <Box sx={{ borderBottom: "1px solid #E6E6E6" }}>
                  <Box
                    onClick={() => setTrustSectionOpen((v) => !v)}
                    sx={SIDEBAR_SECTION_HEADER_SX}
                  >
                    <Box sx={SIDEBAR_SECTION_HEADER_LEFT_SX}>
                      <SectionExpandChevron open={trustSectionOpen} />
                      <TrustAttributesIcon style={{ width: 18, height: 18, display: "block", flexShrink: 0 }} />
                      <Typography
                        sx={{
                          fontFamily: "Lato, sans-serif",
                          fontSize: 13,
                          fontWeight: 700,
                          lineHeight: "20px",
                          color: "#383838",
                        }}
                      >
                        Trust Attributes
                      </Typography>
                    </Box>
                    <Box
                      sx={SIDEBAR_SECTION_HEADER_RIGHT_SX}
                      onClick={(e) => e.stopPropagation()}
                    >
                    {trustSectionOpen ? (
                      <Button
                        size="small"
                        disableRipple
                        onClick={selectAllTrustAttributes}
                        sx={SIDEBAR_SELECT_ALL_SX}
                      >
                        Select all
                      </Button>
                    ) : (
                      <Typography sx={SIDEBAR_SECTION_COUNT_SX}>
                        {selectedAttributes.length} of {TRUST_ATTRIBUTES.length} selected
                      </Typography>
                    )}
                    </Box>
                  </Box>
                  <Collapse in={trustSectionOpen}>
                    <Box sx={{ pb: 0.5 }}>
                      {SIDEBAR_TRUST_ITEMS.map((item) => (
                        <Box key={item.id} sx={SIDEBAR_FILTER_ITEM_SX}>
                          <TestCaseCheckbox
                            checked={selectedAttributes.includes(item.attr)}
                            onChange={() => toggleAttributeFilter(item.attr)}
                            {...SIDEBAR_FILTER_CHECKBOX_PROPS}
                          />
                          <Box
                            sx={{ minWidth: 0, flex: 1, cursor: "pointer" }}
                            onClick={() => toggleAttributeFilter(item.attr)}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Lato, sans-serif",
                                fontSize: 13,
                                fontWeight: 600,
                                lineHeight: "18px",
                                color: "#383838",
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Lato, sans-serif",
                                fontSize: 11,
                                fontWeight: 400,
                                lineHeight: "16px",
                                color: "#707070",
                                mt: 0.25,
                              }}
                            >
                              {String(attributeCounts[item.attr] || 0).padStart(2, "0")} Test Cases
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>

                {/* Risks — Figma 4577:23397 expanded */}
                <Box>
                  <Box
                    onClick={() => setRiskSectionOpen((v) => !v)}
                    sx={{
                      ...SIDEBAR_SECTION_HEADER_SX,
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: "transparent" },
                    }}
                  >
                    <Box sx={SIDEBAR_SECTION_HEADER_LEFT_SX}>
                      <SectionExpandChevron open={riskSectionOpen} />
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          flexShrink: 0,
                          color: "#555555",
                          "& svg path": { stroke: "currentColor" },
                        }}
                      >
                        <RisksWarningIcon style={{ width: 18, height: 18, display: "block" }} />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: "Lato, sans-serif",
                          fontSize: 13,
                          fontWeight: 700,
                          lineHeight: "20px",
                          color: "#383838",
                        }}
                      >
                        Risks
                      </Typography>
                    </Box>
                    <Box
                      sx={SIDEBAR_SECTION_HEADER_RIGHT_SX}
                      onClick={(e) => e.stopPropagation()}
                    >
                    {riskSectionOpen ? (
                      <Button
                        size="small"
                        disableRipple
                        onClick={selectAllRisks}
                        sx={SIDEBAR_SELECT_ALL_SX}
                      >
                        Select all
                      </Button>
                    ) : (
                      <Typography sx={SIDEBAR_SECTION_COUNT_SX}>
                        {selectedRiskIds.length} of {sidebarRisks.length} selected
                      </Typography>
                    )}
                    </Box>
                  </Box>
                  <Collapse in={riskSectionOpen}>
                    <Box sx={{ pb: 0.5 }}>
                      {sidebarRisks.length === 0 ? (
                        <Typography
                          sx={{
                            ...SIDEBAR_RISK_META_SX,
                            px: 2,
                            py: 1.5,
                            pl: SIDEBAR_PANEL_PX,
                          }}
                        >
                          No risks in register.
                        </Typography>
                      ) : (
                        sidebarRisks.map((risk) => {
                          const selected = filterIdInList(selectedRiskIds, risk.id);
                          const count = riskCounts.get(String(risk.id)) || 0;
                          return (
                            <Box key={risk.id} sx={SIDEBAR_FILTER_ITEM_SX}>
                              <TestCaseCheckbox
                                checked={selected}
                                onChange={() => toggleRiskFilter(risk.id)}
                                {...SIDEBAR_FILTER_CHECKBOX_PROPS}
                              />
                              <Box
                                sx={{ minWidth: 0, flex: 1, cursor: "pointer" }}
                                onClick={() => toggleRiskFilter(risk.id)}
                              >
                                <Typography sx={SIDEBAR_RISK_NAME_SX}>{risk.name}</Typography>
                                <Typography sx={SIDEBAR_RISK_META_SX}>
                                  {`${String(count).padStart(2, "0")} Test Cases`}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Collapse>
                </Box>

                {/* Metrics — same pattern as Risks */}
                <Box sx={{ borderTop: "1px solid #E6E6E6" }}>
                  <Box
                    onClick={() => setMetricSectionOpen((v) => !v)}
                    sx={{
                      ...SIDEBAR_SECTION_HEADER_SX,
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: "transparent" },
                    }}
                  >
                    <Box sx={SIDEBAR_SECTION_HEADER_LEFT_SX}>
                      <SectionExpandChevron open={metricSectionOpen} />
                      <MetricsIcon style={{ width: 18, height: 18, display: "block", flexShrink: 0 }} />
                      <Typography
                        sx={{
                          fontFamily: "Lato, sans-serif",
                          fontSize: 13,
                          fontWeight: 700,
                          lineHeight: "20px",
                          color: "#383838",
                        }}
                      >
                        Metrics
                      </Typography>
                    </Box>
                    <Box
                      sx={SIDEBAR_SECTION_HEADER_RIGHT_SX}
                      onClick={(e) => e.stopPropagation()}
                    >
                    {metricSectionOpen ? (
                      <Button
                        size="small"
                        disableRipple
                        onClick={selectAllMetrics}
                        sx={SIDEBAR_SELECT_ALL_SX}
                      >
                        Select all
                      </Button>
                    ) : (
                      <Typography sx={SIDEBAR_SECTION_COUNT_SX}>
                        {selectedMetricIds.length} of {sidebarMetrics.length} selected
                      </Typography>
                    )}
                    </Box>
                  </Box>
                  <Collapse in={metricSectionOpen}>
                    <Box sx={{ pb: 0.5 }}>
                      {sidebarMetrics.length === 0 ? (
                        <Typography
                          sx={{
                            ...SIDEBAR_RISK_META_SX,
                            px: 2,
                            py: 1.5,
                            pl: SIDEBAR_PANEL_PX,
                          }}
                        >
                          No metrics in register.
                        </Typography>
                      ) : (
                        sidebarMetrics.map((metric) => {
                          const selected = filterIdInList(selectedMetricIds, metric.id);
                          const count = metricCounts.get(String(metric.id)) || 0;
                          return (
                            <Box key={metric.id} sx={SIDEBAR_FILTER_ITEM_SX}>
                              <TestCaseCheckbox
                                checked={selected}
                                onChange={() => toggleMetricFilter(metric.id)}
                                {...SIDEBAR_FILTER_CHECKBOX_PROPS}
                              />
                              <Box
                                sx={{ minWidth: 0, flex: 1, cursor: "pointer" }}
                                onClick={() => toggleMetricFilter(metric.id)}
                              >
                                <Typography sx={SIDEBAR_RISK_NAME_SX}>{metric.metricName}</Typography>
                                <Typography sx={SIDEBAR_RISK_META_SX}>
                                  {`${String(count).padStart(2, "0")} Test Cases`}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  width: { xs: "100%", md: COLLAPSED_FILTER_RAIL_WIDTH },
                  flexShrink: 0,
                  alignSelf: "stretch",
                  bgcolor: "#FFFFFF",
                  borderRight: { xs: "none", md: "1px solid #E6E6E6" },
                  borderBottom: { xs: "1px solid #E6E6E6", md: "none" },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "stretch", md: "flex-start" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "row", md: "column" },
                    flexShrink: 0,
                    width: { xs: "100%", md: COLLAPSED_FILTER_RAIL_WIDTH },
                  }}
                >
                <HoverActionTooltip title="Filters" placement={isDesktop ? "right" : "bottom"}>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Open filters"
                  onClick={() => openFiltersPanel("filters")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openFiltersPanel("filters");
                    }
                  }}
                  sx={COLLAPSED_RAIL_CELL_SX}
                >
                  <FilterIcon style={{ width: 16, height: 16, display: "block" }} />
                </Box>
                </HoverActionTooltip>

                <HoverActionTooltip title="Trust Attributes" placement={isDesktop ? "right" : "bottom"}>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Open trust attribute filters"
                  onClick={() => openFiltersPanel("trust")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openFiltersPanel("trust");
                    }
                  }}
                  sx={COLLAPSED_RAIL_CELL_SX}
                >
                  <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TrustAttributesIcon style={{ width: 20, height: 20, display: "block" }} />
                    {selectedAttributes.length > 0 ? (
                      <Box sx={COLLAPSED_RAIL_COUNT_BADGE_SX}>{selectedAttributes.length}</Box>
                    ) : null}
                  </Box>
                </Box>
                </HoverActionTooltip>

                <HoverActionTooltip title="Risks" placement={isDesktop ? "right" : "bottom"}>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Open risk filters"
                  onClick={() => openFiltersPanel("risks")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openFiltersPanel("risks");
                    }
                  }}
                  sx={COLLAPSED_RAIL_CELL_SX}
                >
                  <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <RisksWarningIcon style={{ width: 20, height: 20, display: "block" }} />
                    {selectedRiskIds.length > 0 ? (
                      <Box sx={COLLAPSED_RAIL_COUNT_BADGE_SX}>{selectedRiskIds.length}</Box>
                    ) : null}
                  </Box>
                </Box>
                </HoverActionTooltip>

                <HoverActionTooltip title="Metrics" placement={isDesktop ? "right" : "bottom"}>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Open metric filters"
                  onClick={() => openFiltersPanel("metrics")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openFiltersPanel("metrics");
                    }
                  }}
                  sx={COLLAPSED_RAIL_CELL_SX}
                >
                  <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <MetricsIcon style={{ width: 20, height: 20, display: "block" }} />
                    {selectedMetricIds.length > 0 ? (
                      <Box sx={COLLAPSED_RAIL_COUNT_BADGE_SX}>{selectedMetricIds.length}</Box>
                    ) : null}
                  </Box>
                </Box>
                </HoverActionTooltip>
                </Box>
              </Box>
            )}

            {/* Table area */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden", bgcolor: "#fff" }}>
              <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 1.5, md: 2 }, pb: 1.5, flexShrink: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: { xs: "stretch", sm: "space-between" },
                    gap: 1.5,
                    mb: 1.5,
                  }}
                >
                  <TextField
                    margin="none"
                    placeholder="Search test cases..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ margin: 0, maxHeight: "unset" }}>
                          <SearchIcon style={{ width: 24, height: 24, flexShrink: 0, display: "block" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={TEST_CASES_SEARCH_FIELD_SX}
                  />
            </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", flex: 1 }}>
              {STATUS_TABS.map((t) => (
                <Chip
                  key={t.id}
                  label={t.label}
                  onClick={() => setStatusTab(t.id)}
                  sx={{
                          height: 32,
                    fontWeight: 700,
                          fontSize: 12,
                          borderRadius: "16px",
                          bgcolor: statusTab === t.id ? "#005BF0" : "#FFFFFF",
                    color: statusTab === t.id ? "#fff" : "#344054",
                          border: statusTab === t.id ? "1px solid #005BF0" : "1px solid #D0D5DD",
                          "&:hover": {
                            bgcolor: statusTab === t.id ? "#005BF0" : "#F9FAFB",
                          },
                  }}
                />
              ))}
            </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    {sidebarFiltersActiveCount > 0 ? (
                      <Typography sx={{ fontSize: 12, color: "#005BF0", fontWeight: 700 }}>
                        {sidebarFiltersActiveCount} filter{sidebarFiltersActiveCount === 1 ? "" : "s"} active
                      </Typography>
                    ) : null}
                    <Typography sx={{ fontSize: 12, color: "#667085", fontWeight: 600 }}>
                      {filtered.length} results
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  ...TEST_CASES_VIEW_SCROLL_SX,
                  overflow: "hidden",
                  scrollbarGutter: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    ...TEST_CASES_VIEW_INNER_SX,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                  }}
                >
                <SelectionActionBar
                  store={selectionStore}
                  visibleRows={visibleRows}
                  cases={filtered}
                  statusTab={statusTab}
                  onToggleSelectAll={toggleSelectAll}
                  onDeactivate={handleBulkDeactivate}
                  onReactivate={handleBulkReactivate}
                  onDelete={handleBulkDelete}
                />
                <Box
                  sx={{
                    flex: 1,
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    minHeight: 0,
                    overflowX: "auto",
                    overflowY: "auto",
                    ...METRIC_SECTIONS_STACK_SX,
                  }}
                >
                    {metricGroups.length === 0 ? (
                      <Box
                        sx={{
                          py: 8,
                          textAlign: "center",
                          color: "#98A2B3",
                          fontSize: 13,
                          border: "1px solid #E6E6E6",
                          borderRadius: "4px",
                          bgcolor: "#FFFFFF",
                        }}
                      >
                        No test cases match the current filters.
                      </Box>
                    ) : (
                      metricGroups.map((group) => {
                        const expanded = expandedMetricIds.has(group.metricId);
                        const visibleLimit = getMetricVisibleLimit(group.metricId, group.total);
                        const sectionRows = group.testCases.slice(0, visibleLimit);
                        const showLoadMore = sectionRows.length < group.total;

                        return (
                          <Box key={group.metricId} sx={METRIC_LIST_SECTION_SX}>
                            <Box sx={METRIC_LIST_HEADER_CARD_SX}>
                              <MetricAccordionHeader
                                metricName={group.metricName}
                                count={group.total}
                                verifiedCount={group.verifiedCount}
                                confirmedTotal={group.activeCount}
                                expanded={expanded}
                                onToggle={() => toggleMetricExpanded(group.metricId)}
                                {...(statusTab === "deactivated"
                                  ? {}
                                  : {
                                      onConfirmGroundTruth: () =>
                                        requestConfirmGroundTruthForMetric(group.metricId),
                                      confirmBusy: confirmingMetricId === group.metricId,
                                      confirmGroundTruthDisabled: !group.canConfirmGroundTruth,
                                    })}
                                disableBorderBottom
                              />
                            </Box>
                            <Collapse in={expanded}>
                              <Box sx={METRIC_LIST_TABLE_CARD_SX}>
                                <Box sx={METRIC_LIST_HEADER_CARD_SX}>
                                  <MetricTestCasesTable
                                    testCases={sectionRows}
                                    store={selectionStore}
                                    onToggleSelect={toggleSelectOne}
                                    onToggleSelectSection={() => {
                                      const ids = sectionRows.map((tc) => tc.id);
                                      const allOn = ids.length > 0 && ids.every((id) => selectionStore.has(id));
                                      selectionStore.toggleAll(ids, !allOn);
                                    }}
                                    onView={handleViewTestCase}
                                    onEdit={handleEditTestCase}
                                    onDeleteClick={handleDeleteClick}
                                    onSuspendClick={handleSuspendClick}
                                    onReactivate={handleReactivateClick}
                                    onConfirmGroundTruthClick={handleConfirmGroundTruthForRow}
                                    allowDelete={statusTab !== "deactivated"}
                                  />
                                </Box>
                                {showLoadMore || sectionRows.length > METRIC_INITIAL_VISIBLE ? (
                                  <MetricLoadMoreFooter
                                    shown={sectionRows.length}
                                    total={group.total}
                                    onLoadMore={() => loadMoreMetricCases(group.metricId, group.total)}
                                    onShowLess={() => showLessMetricCases(group.metricId)}
                                  />
                                ) : null}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })
                    )}
                </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={FOOTER_SX}>
        <Button
          disableElevation
          disableRipple
          onClick={() => navigate(`/test-strategy/${projectId}`)}
          sx={FOOTER_GHOST_BTN_SX}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disableElevation
          disableRipple
          disabled={generating || !hasAny}
          onClick={() => navigate(`/test-suites/${projectId}`)}
          sx={GO_TO_TEST_SUITES_BTN_SX}
        >
          Go To Test Suites
        </Button>
      </Box>

      <AddTestCaseDialog
        open={testCaseFlyout.open}
        onClose={closeTestCaseFlyout}
        onSave={handleEditSave}
        metrics={metrics}
        risks={risks}
        editingTestCase={testCaseFlyout.testCase}
        viewOnly={testCaseFlyout.viewOnly}
        isContentGenerator={isContentGenerator}
        projectId={projectId}
      />

      <DeleteTestCaseDialog
        testCases={deleteDialogItems}
        onClose={() => {
          if (deleting) return;
          setTestCaseToDelete(null);
          setBulkAction(null);
        }}
        onConfirm={handleDeleteConfirm}
        submitting={deleting}
      />

      <SuspendTestCaseDialog
        testCases={suspendDialogItems}
        onClose={() => {
          if (suspending) return;
          setTestCaseToSuspend(null);
          setBulkAction(null);
        }}
        onConfirm={
          bulkAction === "reactivate" || testCaseToSuspend?.status === "deactivated"
            ? handleReactivateConfirm
            : handleSuspendConfirm
        }
        variant={
          bulkAction === "reactivate" || testCaseToSuspend?.status === "deactivated"
            ? "reactivate"
            : "suspend"
        }
        submitting={suspending}
      />

      <MixedSourceSelectionDialog
        open={mixedSelectionOpen}
        onClose={() => setMixedSelectionOpen(false)}
      />

      <ConfirmGroundTruthDialog
        open={Boolean(groundTruthDialog)}
        metricName={groundTruthDialog?.metricName}
        confirmCount={groundTruthDialog?.confirmCount ?? 0}
        sourceSummary={groundTruthDialog?.sourceSummary ?? ""}
        otherMetricsLabel={groundTruthDialog?.otherMetricsLabel ?? ""}
        onClose={() => {
          if (confirmingMetricId) return;
          setGroundTruthDialog(null);
        }}
        onConfirm={executeConfirmGroundTruthForMetric}
        submitting={Boolean(confirmingMetricId)}
      />

      <ConfirmGroundTruthRowDialog
        testCase={rowGroundTruthDialog}
        isContentGenerator={isContentGenerator}
        onClose={() => {
          if (confirmingRowId) return;
          setRowGroundTruthDialog(null);
        }}
        onConfirm={executeConfirmGroundTruthForRow}
        submitting={Boolean(confirmingRowId)}
      />

      <Snackbar open={Boolean(error)} onClose={() => setError("")} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>
      <IntakeSuccessToast
        open={Boolean(toast)}
        message={toast}
        onClose={() => setToast("")}
        left={24}
      />
    </Box>
    </SelectDropdownTheme>
  );
}
